import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, ChevronDown, Flag, Info, List, Loader2, MoreHorizontal, Plus, RotateCcw, Sparkles, Trash2, User, Users, X } from 'lucide-react';
import { api } from '../utils/client';
import { getCookie, requestNotificationsRefresh } from '../utils/utils';
import type { Blueprint, MethodologyFlag } from '../types/blueprints';
import type { WorkspacePayload } from '../types/workspace';
import ConceptGenerationPage from './ConceptGenerationPage';
import InnovationWorkspaceButton from './InnovationWorkspaceButton';
import RecipeCardTabControls, {
  type RecipeCardBrandOption,
  type RecipeCardFormatOption,
  type RecipeCardTabItem,
  recipeAddAttributePillClass,
  recipeAddTabButtonClass,
  recipeTabActiveClass,
  recipeTabInactiveClass,
} from './RecipeCardTabControls';
import DropdownMenu, { MenuItem } from './DropdownMenu';
import { useCaps } from '../contexts/CapabilitiesContext';
import CommercialProductList from './CommercialProductList';
import { queryKeys } from '../queryKeys';
import {
  MarketplaceProduct,
  marketplaceProductsQuery,
} from '../utils/marketplaceProducts';
import { fetchWorkspace, invalidateWorkspaceQueries, workspaceQueryOptions } from '../utils/workspaceApi';

export interface RecipeFocusTarget {
  step: 2 | 3;
  combinationId?: number;
  artifactId?: number;
  recipePageView?: 'recipe-card' | 'product-idea';
}

interface Props {
  projectId: number;
  blueprintId?: number;
  initialStep?: number;
  /** When true, step 1 lives in the Innovation Workspace drawer; builder opens on recipe card (step 2). */
  workspaceMode?: boolean;
  recipeFocus?: RecipeFocusTarget | null;
  onRecipeFocusConsumed?: () => void;
  onClose: () => void;
  onBackToWorkspace?: () => void;
  onViewInnovationPlatform?: () => void;
  onNavigateToMarketplaceProduct?: (categoryId: number, productId: number) => void;
  onSuccess?: (blueprint: Blueprint) => void;
  /** Bumped when the Innovation Workspace drawer saves platform/job data. */
  workspaceRefreshKey?: number;
}

interface RecipeAttributeSet {
  id: number;
  attribute_key?: string;
  attribute_name: string;
  description_text?: string;
  details_text: string;
  constraints_text: string;
  is_user_added: boolean;
  has_been_evidenced?: boolean;
  tab_state_id?: number | null;
}

interface RecipeAttributeOption {
  id: number;
  attribute_set: number;
  option_text: string;
  option_notes?: string;
  generation_source: 'ai' | 'study' | 'user';
  is_user_added: boolean;
  combination?: number | null;
  output_version?: number | null;
  display_order: number;
  study_percentage?: number | string | null;
  ai_category?: '' | 'established' | 'emerging' | 'breakthrough';
}

function sortRecipeAttributeOptions(options: RecipeAttributeOption[]): RecipeAttributeOption[] {
  return [...options].sort((a, b) => {
    const aIsStudy = a.generation_source === 'study' && a.study_percentage != null;
    const bIsStudy = b.generation_source === 'study' && b.study_percentage != null;
    if (aIsStudy && bIsStudy) {
      return Number(b.study_percentage) - Number(a.study_percentage);
    }
    return a.display_order - b.display_order;
  });
}

function formatRecipeOptionLabel(option: RecipeAttributeOption): string {
  if (option.generation_source === 'study' && option.study_percentage != null) {
    const percent = Number(option.study_percentage);
    const label = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
    return `${option.option_text} ${label}%`;
  }
  return option.option_text;
}

interface RecipeAttributeOutputVersion {
  id: number;
  attribute_set: number;
  version_number: number;
  label: string;
  is_current: boolean;
  details_text_snapshot: string;
  constraints_text_snapshot: string;
  options: RecipeAttributeOption[];
  created_at?: string;
}

const DETAILS_OPTION_NOTE = 'details_input';

type RecipeCardBuilderServiceId =
  | 'catalyx-ai'
  | 'catalyx-crowd'
  | 'build-by-attribute-crowd'
  | 'input-own';

const recipeCardBuilderExploreClass =
  'shrink-0 px-6 py-2.5 rounded-full bg-[#655FA8] text-white text-sm font-semibold hover:bg-[#544fa0] transition-colors whitespace-nowrap disabled:opacity-50';

const recipeCardBuilderStartClass =
  'shrink-0 px-6 py-2.5 rounded-full bg-[#43C8F5] text-white text-sm font-semibold hover:bg-[#2A9CC4] transition-colors whitespace-nowrap disabled:opacity-50';

const RAJA_PROJECT_NAME = 'raja';
/** Matches backend RAJA_MULTI_OPTION_DELIMITER — one DB row per attribute, multiple options in snapshot text. */
const RAJA_MULTI_OPTION_DELIMITER = ' ◆ ';

function parseDetailOptions(text: string): string[] {
  return text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

interface RecipeCombinationSelectionPayload {
  attribute_set: number;
  option: number;
  snapshot_option_text?: string;
}

interface RecipeCombinationSelection extends RecipeCombinationSelectionPayload {
  id?: number;
  snapshot_option_text?: string;
}

interface RecipeCombination {
  id: number;
  combination_name: string;
  tab?: number | null;
  created_at?: string;
  selections: RecipeCombinationSelection[];
}

const sortCombinationsOldestFirst = (combos: RecipeCombination[]) =>
  [...combos].sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return a.id - b.id;
  });

const getNextCombinationName = (combos: RecipeCombination[]) => `Combination ${combos.length + 1}`;

type SelectedOptionsByAttribute = Record<number, number[]>;
/** Per-combination view of which AI output version is shown for each attribute. */
type ActiveOutputVersionByCombination = Record<number, Record<number, number | null>>;

const getActiveOutputVersionId = (
  byCombination: ActiveOutputVersionByCombination,
  combinationId: number | null,
  attributeId: number
): number | null => {
  if (combinationId == null) return null;
  return byCombination[combinationId]?.[attributeId] ?? null;
};

const setActiveOutputVersionForCombination = (
  byCombination: ActiveOutputVersionByCombination,
  combinationId: number,
  attributeId: number,
  versionId: number | null
): ActiveOutputVersionByCombination => ({
  ...byCombination,
  [combinationId]: {
    ...(byCombination[combinationId] || {}),
    [attributeId]: versionId,
  },
});

/** Infer which output version a combination was using from its saved option IDs. */
const inferOutputVersionsFromSelections = (
  selections: SelectedOptionsByAttribute,
  historyByAttr: Record<number, RecipeAttributeOutputVersion[]>
): Record<number, number | null> => {
  const inferred: Record<number, number | null> = {};
  Object.entries(selections).forEach(([attrIdStr, optionIds]) => {
    const attrId = Number(attrIdStr);
    if (!optionIds.length) return;
    const versions = historyByAttr[attrId] || [];
    for (const version of versions) {
      const versionOptIds = new Set((version.options || []).map((opt) => opt.id));
      if (optionIds.some((id) => versionOptIds.has(id))) {
        inferred[attrId] = version.id;
        break;
      }
    }
  });
  return inferred;
};

const combinationSelectionsFrom = (
  combination: RecipeCombination,
  attributeOptions: RecipeAttributeOption[],
  isRaja: boolean,
): SelectedOptionsByAttribute => {
  const next: SelectedOptionsByAttribute = {};
  combination.selections.forEach((selection) => {
    if (!isRaja) {
      if (!next[selection.attribute_set]) {
        next[selection.attribute_set] = selection.option ? [selection.option] : [];
      }
      return;
    }

    if (next[selection.attribute_set]) return;

    const optionsForAttr = attributeOptions.filter((o) => o.attribute_set === selection.attribute_set);
    const snapshot = (selection.snapshot_option_text || '').trim();

    if (snapshot.includes(RAJA_MULTI_OPTION_DELIMITER)) {
      const ids = snapshot
        .split(RAJA_MULTI_OPTION_DELIMITER)
        .map((part) => part.trim())
        .map((part) => optionsForAttr.find((o) => (o.option_text || '').trim() === part)?.id)
        .filter((id): id is number => Boolean(id));
      if (ids.length > 0) {
        next[selection.attribute_set] = ids;
      } else if (selection.option) {
        next[selection.attribute_set] = [selection.option];
      }
    } else if (selection.option) {
      next[selection.attribute_set] = [selection.option];
    }
  });
  return next;
};

const selectionMapsEqual = (a: SelectedOptionsByAttribute, b: SelectedOptionsByAttribute) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const left = [...(a[Number(key)] || [])].sort((x, y) => x - y);
    const right = [...(b[Number(key)] || [])].sort((x, y) => x - y);
    if (left.length !== right.length || !left.every((value, index) => value === right[index])) {
      return false;
    }
  }
  return true;
};

const autoSelectSingleOptions = (
  selections: SelectedOptionsByAttribute,
  attributeOptions: RecipeAttributeOption[],
  attributeIds: number[] = [],
): SelectedOptionsByAttribute => {
  const next: SelectedOptionsByAttribute = { ...selections };
  const ids = attributeIds.length
    ? attributeIds
    : Array.from(new Set(attributeOptions.map((opt) => opt.attribute_set)));
  for (const attrId of ids) {
    if ((next[attrId] || []).length > 0) continue;
    const optionsForAttr = attributeOptions.filter((opt) => opt.attribute_set === attrId);
    if (optionsForAttr.length === 1) {
      next[attrId] = [optionsForAttr[0].id];
    }
  }
  return next;
};

const isOptionSelected = (
  selectedByAttribute: SelectedOptionsByAttribute,
  attributeSetId: number,
  optionId: number
) => (selectedByAttribute[attributeSetId] || []).includes(optionId);

const toggleOptionSelection = (
  selectedByAttribute: SelectedOptionsByAttribute,
  attributeSetId: number,
  optionId: number
): SelectedOptionsByAttribute => {
  const current = selectedByAttribute[attributeSetId] || [];
  const nextForAttribute = current.includes(optionId)
    ? current.filter((id) => id !== optionId)
    : [...current, optionId];
  return { ...selectedByAttribute, [attributeSetId]: nextForAttribute };
};

const hasAnyCombinationSelection = (selectedByAttribute: SelectedOptionsByAttribute) =>
  Object.values(selectedByAttribute).some((optionIds) => optionIds.length > 0);

interface ResearchJobStatusResponse {
  found: boolean;
  status: string;
  job_id: string;
  market?: string;
  country?: string;
  ready?: boolean;
  check_type?: 'research_job' | 'market_synthesis';
  message?: string;
}

interface JtbdLatestResponse {
  found?: boolean;
  id?: number | null;
  jtbd_statements?: string[];
  generation_allowed?: boolean;
  source?: 'blueprint' | 'project';
  message?: string;
}

interface JtbdGenerateResponse {
  id: number;
  project_id?: number;
  blueprint_id?: number;
  jtbd_statements?: string[];
}

interface TensionItem {
  tension_text?: string;
  metadata?: Record<string, any>;
}

interface TensionsLatestResponse {
  found?: boolean;
  id?: number | null;
  tensions?: TensionItem[];
  generation_allowed?: boolean;
  source?: 'blueprint' | 'project';
  message?: string;
}

interface TensionsGenerateResponse {
  id: number;
  project_id?: number;
  blueprint_id?: number;
  tensions?: TensionItem[];
}

interface BenefitItem {
  benefit_text?: string;
  metadata?: { benefit_deep_dive?: string };
}

interface BenefitsLatestResponse {
  found?: boolean;
  id?: number | null;
  benefits?: BenefitItem[];
  generation_allowed?: boolean;
  source?: 'blueprint' | 'project';
  message?: string;
}

interface BenefitsGenerateResponse {
  id: number;
  project_id?: number;
  blueprint_id?: number;
  benefits?: BenefitItem[];
}

const resolveBlueprintMarket = (
  marketState: string,
  savedBlueprintMarket?: string | null
) => (marketState.trim() || savedBlueprintMarket?.trim() || '').trim();

const isResearchJobCompleted = (payload: ResearchJobStatusResponse | null | undefined) => {
  if (!payload?.found) return false;
  if (payload.ready === true) return true;
  return (payload.status || '').trim().toLowerCase() === 'completed';
};

type AssistKind = 'job' | 'tension' | 'benefit' | 'other';

const JOB_ASSIST_READY_TITLE = 'Jobs to be done';
const JOB_ASSIST_READY_BODY =
  'Would you like to generate 15 Jobs to be done tailored just for your project with our AI.';
const TENSION_ASSIST_READY_TITLE = 'Tensions';
const TENSION_ASSIST_READY_BODY = 'Generate tensions for your Job to be done?';
const BENEFIT_ASSIST_READY_TITLE = 'Benefits';
const BENEFIT_ASSIST_READY_BODY = 'Generate benefits for your tension?';

const TOTAL_STEPS = 3;
const ATTRIBUTE_AUTOSAVE_DEBOUNCE_MS = 900;

function formatMethodologyTag(evidenced: boolean | null | undefined): string {
  if (evidenced === true) return 'User Added • Evidenced';
  if (evidenced === false) return 'User Added • Not Evidenced';
  return 'User Added';
}

function MethodologyFlagIcon({ type }: { type: MethodologyFlag['type'] }) {
  const iconClass = 'w-4 h-4 shrink-0 text-gray-400';
  if (type === 'exploratory_input') {
    return <Flag className={iconClass} aria-hidden />;
  }
  if (type === 'innovation_platform') {
    return <Users className={iconClass} aria-hidden />;
  }
  return <User className={iconClass} aria-hidden />;
}

const BlueprintBuilderV2: React.FC<Props> = ({
  projectId,
  blueprintId,
  initialStep = 1,
  workspaceMode = false,
  recipeFocus = null,
  onRecipeFocusConsumed,
  onClose,
  onBackToWorkspace,
  onViewInnovationPlatform,
  onNavigateToMarketplaceProduct,
  onSuccess,
  workspaceRefreshKey = 0,
}) => {
  const { can, loading, error: capsError } = useCaps();
  const queryClient = useQueryClient();
  const canCreateBlueprint = can('blueprints_edit');
  const canSaveBlueprint = can('blueprints_edit');
  const canGenerateArtifacts = can('ai_generate') && canSaveBlueprint;

  const [step, setStep] = useState(initialStep);
  const [isSaving, setIsSaving] = useState(false);
  const [step1ContinueBusy, setStep1ContinueBusy] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdBlueprint, setCreatedBlueprint] = useState<Blueprint | null>(null);

  const [projectDisplayName, setProjectDisplayName] = useState('');
  const isRajaProject = useMemo(() => {
    const name = (projectDisplayName || createdBlueprint?.project_name || '').trim().toLowerCase();
    return name === RAJA_PROJECT_NAME;
  }, [projectDisplayName, createdBlueprint?.project_name]);
  const [projectMarkets, setProjectMarkets] = useState<string[]>([]);
  const [methodologyFlags, setMethodologyFlags] = useState<MethodologyFlag[]>([]);
  const [showAllMethodologyNotes, setShowAllMethodologyNotes] = useState(false);
  const [recipeCardTabs, setRecipeCardTabs] = useState<RecipeCardTabItem[]>([]);
  const [activeRecipeTabId, setActiveRecipeTabId] = useState<number | null>(null);
  const [recipeCardBrands, setRecipeCardBrands] = useState<RecipeCardBrandOption[]>([]);
  const [recipeCardFormats, setRecipeCardFormats] = useState<RecipeCardFormatOption[]>([]);
  const [recipeTabsBusy, setRecipeTabsBusy] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [market, setMarket] = useState('');
  const [marketLocked, setMarketLocked] = useState(false);
  const [isMarketAutosaving, setIsMarketAutosaving] = useState(false);
  /** True only after Configure Recipe Card (or when editing a blueprint that already has recipe attributes). */
  const [innovationPlatformLocked, setInnovationPlatformLocked] = useState(false);
  const [job, setJob] = useState('');
  const [tension, setTension] = useState('');
  const [benefit, setBenefit] = useState('');
  const [permission, setPermission] = useState('');

  // New V2-only input fields (mapped into generation prompt context)
  const [targetAudience, setTargetAudience] = useState('');
  const [occasionContext, setOccasionContext] = useState('');
  const [reasonsToBelieve, setReasonsToBelieve] = useState('');
  const [innovationGuardrails, setInnovationGuardrails] = useState('');

  const [attributes, setAttributes] = useState<RecipeAttributeSet[]>([]);
  const [generationMode, setGenerationMode] = useState<'ai' | 'crowd' | null>(null);
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeDescription, setNewAttributeDescription] = useState('');
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);
  const [showGenerationSummaryModal, setShowGenerationSummaryModal] = useState(false);
  const [showRecipeCardBuilderModal, setShowRecipeCardBuilderModal] = useState(false);
  const [recipeCardBuilderProducts, setRecipeCardBuilderProducts] = useState<MarketplaceProduct[]>([]);
  const [recipeCardBuilderProductsLoading, setRecipeCardBuilderProductsLoading] = useState(false);
  const [attributeOptions, setAttributeOptions] = useState<RecipeAttributeOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [optionDrafts, setOptionDrafts] = useState<Record<number, string>>({});
  const [showExploratoryModal, setShowExploratoryModal] = useState(false);
  const [exploratoryAttributeId, setExploratoryAttributeId] = useState<number | null>(null);
  const [exploratoryText, setExploratoryText] = useState('');
  const [exploratoryEvidenced, setExploratoryEvidenced] = useState(false);
  const [showRegenerateAttributeModal, setShowRegenerateAttributeModal] = useState(false);
  const [regenerateAttributeId, setRegenerateAttributeId] = useState<number | null>(null);
  const [regenerateDetailsText, setRegenerateDetailsText] = useState('');
  const [regenerateConstraintsText, setRegenerateConstraintsText] = useState('');
  const [isRegeneratingAttribute, setIsRegeneratingAttribute] = useState(false);
  const [attributeOutputHistory, setAttributeOutputHistory] = useState<Record<number, RecipeAttributeOutputVersion[]>>({});
  const [activeOutputVersionByCombination, setActiveOutputVersionByCombination] =
    useState<ActiveOutputVersionByCombination>({});
  const [openOutputMenuAttributeId, setOpenOutputMenuAttributeId] = useState<number | null>(null);
  const [showRemoveAttributeModal, setShowRemoveAttributeModal] = useState(false);
  const [pendingRemoveAttributeId, setPendingRemoveAttributeId] = useState<number | null>(null);
  const [showDeleteTabModal, setShowDeleteTabModal] = useState(false);
  const [pendingDeleteTabId, setPendingDeleteTabId] = useState<number | null>(null);
  const [selectedOptionByAttribute, setSelectedOptionByAttribute] = useState<SelectedOptionsByAttribute>({});
  const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [isSavingDraftRecipe, setIsSavingDraftRecipe] = useState(false);
  const [savedCombinations, setSavedCombinations] = useState<RecipeCombination[]>([]);
  const [activeCombinationId, setActiveCombinationId] = useState<number | null>(null);
  const [recipePageView, setRecipePageView] = useState<'recipe-card' | 'product-idea'>('recipe-card');
  const [conceptArtifactId, setConceptArtifactId] = useState<number | null>(null);
  const [openCombinationMenuId, setOpenCombinationMenuId] = useState<number | null>(null);
  const [renamingCombinationId, setRenamingCombinationId] = useState<number | null>(null);
  const [renameCombinationValue, setRenameCombinationValue] = useState('');
  const [showDeleteCombinationModal, setShowDeleteCombinationModal] = useState(false);
  const [pendingDeleteCombinationId, setPendingDeleteCombinationId] = useState<number | null>(null);
  const [showProductIdeaGeneratorModal, setShowProductIdeaGeneratorModal] = useState(false);
  const [productIdeaGeneratorProducts, setProductIdeaGeneratorProducts] = useState<MarketplaceProduct[]>([]);
  const [productIdeaGeneratorProductsLoading, setProductIdeaGeneratorProductsLoading] = useState(false);
  const [showProductIdeaValidatorModal, setShowProductIdeaValidatorModal] = useState(false);
  const [productIdeaValidatorProducts, setProductIdeaValidatorProducts] = useState<MarketplaceProduct[]>([]);
  const [productIdeaValidatorProductsLoading, setProductIdeaValidatorProductsLoading] = useState(false);
  const combinationMenuButtonRef = useRef<HTMLButtonElement>(null);
  const recipeFocusAppliedRef = useRef<string | null>(null);
  const recipeFocusRef = useRef(recipeFocus);
  const [savingAttributeId, setSavingAttributeId] = useState<number | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const saveNoticeHideTimeoutRef = useRef<number | null>(null);
  const attributesRef = useRef(attributes);
  const createdBlueprintRef = useRef(createdBlueprint);
  const stepRef = useRef(step);
  const canSaveBlueprintRef = useRef(canSaveBlueprint);
  const attributeAutosaveTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const lastSavedAttributeFieldsRef = useRef<
    Map<number, { details_text: string; constraints_text: string; has_been_evidenced: boolean }>
  >(new Map());
  const attributeSaveInFlightRef = useRef<Map<number, Promise<void>>>(new Map());
  const combinationOptionsCacheRef = useRef<Map<number, RecipeAttributeOption[]>>(new Map());
  const combinationAutosaveTimeoutRef = useRef<number | null>(null);
  const [isRecipeAiGenerating, setIsRecipeAiGenerating] = useState(false);
  const [showUnderConstructionModal, setShowUnderConstructionModal] = useState(false);
  const [showCrowdWorkInProgressModal, setShowCrowdWorkInProgressModal] = useState(false);
  const [showFormatIdeasWorkInProgressModal, setShowFormatIdeasWorkInProgressModal] = useState(false);
  const [showRecipeCardValidatorModal, setShowRecipeCardValidatorModal] = useState(false);
  const [recipeCardValidatorProducts, setRecipeCardValidatorProducts] = useState<MarketplaceProduct[]>([]);
  const [recipeCardValidatorProductsLoading, setRecipeCardValidatorProductsLoading] = useState(false);
  const [assistCheckBusy, setAssistCheckBusy] = useState(false);
  const [assistResearchCompleted, setAssistResearchCompleted] = useState(false);
  const [assistModalMessage, setAssistModalMessage] = useState<string | null>(null);
  const [assistRequiresJobLock, setAssistRequiresJobLock] = useState(false);
  const [assistRequiresTensionLock, setAssistRequiresTensionLock] = useState(false);
  const [assistKind, setAssistKind] = useState<AssistKind>('other');
  const [jtbdStatements, setJtbdStatements] = useState<string[]>([]);
  const [jtbdGenerationAllowed, setJtbdGenerationAllowed] = useState(true);
  const [jtbdGenerateBusy, setJtbdGenerateBusy] = useState(false);
  const [showJtbdPickerModal, setShowJtbdPickerModal] = useState(false);
  const [jobLocked, setJobLocked] = useState(false);
  const [jobLockBusy, setJobLockBusy] = useState(false);
  const [tensionStatements, setTensionStatements] = useState<TensionItem[]>([]);
  const [tensionsGenerationAllowed, setTensionsGenerationAllowed] = useState(true);
  const [tensionGenerateBusy, setTensionGenerateBusy] = useState(false);
  const [showTensionPickerModal, setShowTensionPickerModal] = useState(false);
  const [tensionLocked, setTensionLocked] = useState(false);
  const [tensionLockBusy, setTensionLockBusy] = useState(false);
  const [benefitStatements, setBenefitStatements] = useState<BenefitItem[]>([]);
  const [benefitsGenerationAllowed, setBenefitsGenerationAllowed] = useState(true);
  const [benefitGenerateBusy, setBenefitGenerateBusy] = useState(false);
  const [showBenefitPickerModal, setShowBenefitPickerModal] = useState(false);

  useEffect(() => {
    attributesRef.current = attributes;
  }, [attributes]);

  useEffect(() => {
    createdBlueprintRef.current = createdBlueprint;
  }, [createdBlueprint]);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    canSaveBlueprintRef.current = canSaveBlueprint;
  }, [canSaveBlueprint]);

  useEffect(() => {
    recipeFocusRef.current = recipeFocus;
  }, [recipeFocus]);

  useEffect(() => {
    if (step === 2) return;
    attributeAutosaveTimersRef.current.forEach((timer) => clearTimeout(timer));
    attributeAutosaveTimersRef.current.clear();
  }, [step]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (stepRef.current !== 2) return;
      flushAllAttributeAutosaves({ keepalive: true });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (stepRef.current === 2) {
        flushAllAttributeAutosaves({ keepalive: true });
      }
      attributeAutosaveTimersRef.current.forEach((timer) => clearTimeout(timer));
      attributeAutosaveTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    activeRecipeTabIdRef.current = activeRecipeTabId;
  }, [activeRecipeTabId]);

  const syncLastSavedAttributeFields = (attrs: RecipeAttributeSet[]) => {
    const map = new Map<
      number,
      { details_text: string; constraints_text: string; has_been_evidenced: boolean }
    >();
    attrs.forEach((attr) => {
      if (!attr.id) return;
      map.set(attr.id, {
        details_text: attr.details_text || '',
        constraints_text: attr.constraints_text || '',
        has_been_evidenced: Boolean(attr.has_been_evidenced),
      });
    });
    lastSavedAttributeFieldsRef.current = map;
  };

  const asBlueprint = (bp: NonNullable<WorkspacePayload['blueprint']>): Blueprint => ({
    ...bp,
    attributes: bp.attributes ?? [],
    artifacts: bp.artifacts ?? [],
  });

  const applyWorkspacePayload = (data: WorkspacePayload): Blueprint | null => {
    const mkts = Array.isArray(data.project?.markets) ? data.project.markets : [];
    setProjectMarkets(mkts);
    setProjectDisplayName(data.project?.name || '');

    if (!data.blueprint) return null;
    const bp = asBlueprint(data.blueprint);
    setCreatedBlueprint(bp);
    setName(bp.name || '');
    setDescription(bp.description || '');
    setMarket(bp.market || '');
    setMarketLocked(Boolean((bp.market || '').trim()));
    setJob(bp.job_to_be_done || '');
    setJobLocked(Boolean(bp.job_to_be_done_locked));
    setTension(bp.tension || '');
    setTensionLocked(Boolean(bp.tension_locked));
    setBenefit(bp.benefit || '');
    setPermission(bp.permission || '');
    setRecipeCardBrands(Array.isArray(data.recipe_card_brands) ? data.recipe_card_brands : []);
    setRecipeCardFormats(Array.isArray(data.recipe_card_formats) ? data.recipe_card_formats : []);
    const nextTabs = Array.isArray(data.recipe_card_tabs) ? data.recipe_card_tabs : [];
    setRecipeCardTabs(nextTabs);
    const preferred = data.active_tab_id;
    const nextActive =
      preferred && nextTabs.some((tab) => tab.id === preferred)
        ? preferred
        : nextTabs[0]?.id ?? null;
    activeRecipeTabIdRef.current = nextActive;
    setActiveRecipeTabId(nextActive);
    const nextAttrs = (Array.isArray(data.attributes) ? data.attributes : []) as RecipeAttributeSet[];
    setAttributes(nextAttrs);
    syncLastSavedAttributeFields(nextAttrs);
    if (nextAttrs.length > 0) {
      recipeAttributesHydratedRef.current = bp.id;
    }
    setMethodologyFlags(Array.isArray(data.methodology_flags) ? data.methodology_flags : []);
    return bp;
  };

  const touchWorkspaceCache = () => {
    void invalidateWorkspaceQueries(projectId);
  };

  const attributeFieldsDirty = (attr: RecipeAttributeSet) => {
    const last = lastSavedAttributeFieldsRef.current.get(attr.id);
    if (!last) return true;
    return (
      (attr.details_text || '') !== last.details_text ||
      (attr.constraints_text || '') !== last.constraints_text ||
      Boolean(attr.has_been_evidenced) !== last.has_been_evidenced
    );
  };

  const clearAttributeAutosaveTimer = (attrId: number) => {
    const timer = attributeAutosaveTimersRef.current.get(attrId);
    if (timer != null) {
      clearTimeout(timer);
      attributeAutosaveTimersRef.current.delete(attrId);
    }
  };

  const persistAttributeSetKeepalive = (blueprintId: number, attr: RecipeAttributeSet) => {
    if (!attributeFieldsDirty(attr)) return;
    const csrftoken = getCookie('csrftoken');
    const tabId = activeRecipeTabIdRef.current;
    const url = tabId
      ? `/api/blueprints/${blueprintId}/recipe-card-tabs/${tabId}/attributes/${attr.id}/`
      : `/api/blueprints/${blueprintId}/recipe-attributes/${attr.id}/`;
    void fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken || '',
      },
      body: JSON.stringify({
        details_text: attr.details_text || '',
        constraints_text: attr.constraints_text || '',
        ...(tabId ? { has_been_evidenced: Boolean(attr.has_been_evidenced) } : {}),
      }),
    });
  };

  const persistAttributeSet = async (
    attrId: number,
    { silent = false, keepalive = false }: { silent?: boolean; keepalive?: boolean } = {}
  ): Promise<boolean> => {
    const blueprintId = createdBlueprintRef.current?.id;
    if (!blueprintId || !canSaveBlueprintRef.current) return false;

    const attr = attributesRef.current.find((a) => a.id === attrId);
    if (!attr || !attributeFieldsDirty(attr)) return true;

    if (keepalive) {
      persistAttributeSetKeepalive(blueprintId, attr);
      lastSavedAttributeFieldsRef.current.set(attrId, {
        details_text: attr.details_text || '',
        constraints_text: attr.constraints_text || '',
        has_been_evidenced: Boolean(attr.has_been_evidenced),
      });
      return true;
    }

    const inFlight = attributeSaveInFlightRef.current.get(attrId);
    if (inFlight) {
      await inFlight;
      const latest = attributesRef.current.find((a) => a.id === attrId);
      if (!latest || !attributeFieldsDirty(latest)) return true;
    }

    const tabId = activeRecipeTabIdRef.current;
    const savePromise = (async () => {
      const current = attributesRef.current.find((a) => a.id === attrId);
      if (!current || !attributeFieldsDirty(current)) return;

      setSavingAttributeId(attrId);
      try {
        const previousDetails = lastSavedAttributeFieldsRef.current.get(attrId)?.details_text || '';
        const url = tabId
          ? `/api/blueprints/${blueprintId}/recipe-card-tabs/${tabId}/attributes/${attrId}/`
          : `/api/blueprints/${blueprintId}/recipe-attributes/${attrId}/`;
        const updated = await api<RecipeAttributeSet>(url, {
          method: 'PATCH',
          body: JSON.stringify({
            details_text: current.details_text || '',
            constraints_text: current.constraints_text || '',
            ...(tabId ? { has_been_evidenced: Boolean(current.has_been_evidenced) } : {}),
          }),
        });
        lastSavedAttributeFieldsRef.current.set(attrId, {
          details_text: updated.details_text || '',
          constraints_text: updated.constraints_text || '',
          has_been_evidenced: Boolean(updated.has_been_evidenced),
        });
        setAttributes((prev) =>
          prev.map((a) =>
            a.id === attrId
              ? {
                  ...a,
                  details_text: updated.details_text || '',
                  constraints_text: updated.constraints_text || '',
                  has_been_evidenced: Boolean(updated.has_been_evidenced),
                  tab_state_id: updated.tab_state_id ?? a.tab_state_id,
                }
              : a
          )
        );
        void loadMethodologyFlags(blueprintId, activeRecipeTabIdRef.current);
        if ((current.details_text || '') !== previousDetails) {
          try {
            await syncDetailsAsUserOptions(blueprintId);
          } catch {
            // Options sync is best-effort after details save.
          }
        }
        if (!silent) {
          showTransientSaveNotice(`Saved "${current.attribute_name}"`, 3000);
        }
      } catch (e: any) {
        if (!silent) {
          setError(e.message || 'Failed to save attribute');
        }
        throw e;
      } finally {
        setSavingAttributeId((currentId) => (currentId === attrId ? null : currentId));
      }
    })();

    attributeSaveInFlightRef.current.set(attrId, savePromise);
    try {
      await savePromise;
      return true;
    } catch {
      return false;
    } finally {
      attributeSaveInFlightRef.current.delete(attrId);
    }
  };

  const scheduleAttributeAutosave = (attrId: number) => {
    if (!createdBlueprintRef.current?.id || !canSaveBlueprintRef.current) return;
    clearAttributeAutosaveTimer(attrId);
    attributeAutosaveTimersRef.current.set(
      attrId,
      setTimeout(() => {
        attributeAutosaveTimersRef.current.delete(attrId);
        void persistAttributeSet(attrId, { silent: true });
      }, ATTRIBUTE_AUTOSAVE_DEBOUNCE_MS)
    );
  };

  const flushAttributeAutosave = (attrId: number) => {
    clearAttributeAutosaveTimer(attrId);
    void persistAttributeSet(attrId, { silent: true });
  };

  const flushAllAttributeAutosaves = async ({ keepalive = false }: { keepalive?: boolean } = {}) => {
    const dirtyIds = attributesRef.current
      .filter((attr) => attr.id && attributeFieldsDirty(attr))
      .map((attr) => attr.id);
    const pendingIds = [...attributeAutosaveTimersRef.current.keys()];
    const attrIds = [...new Set([...dirtyIds, ...pendingIds])];

    pendingIds.forEach((attrId) => clearAttributeAutosaveTimer(attrId));
    if (attrIds.length === 0) return;

    if (keepalive) {
      const blueprintId = createdBlueprintRef.current?.id;
      if (!blueprintId) return;
      attributesRef.current.forEach((attr) => {
        if (attr.id && attributeFieldsDirty(attr)) {
          persistAttributeSetKeepalive(blueprintId, attr);
        }
      });
      return;
    }

    await Promise.all(attrIds.map((attrId) => persistAttributeSet(attrId, { silent: true })));
  };

  const showTransientSaveNotice = (message: string, durationMs: number) => {
    if (saveNoticeHideTimeoutRef.current != null) {
      window.clearTimeout(saveNoticeHideTimeoutRef.current);
      saveNoticeHideTimeoutRef.current = null;
    }
    setSaveNotice(message);
    saveNoticeHideTimeoutRef.current = window.setTimeout(() => {
      setSaveNotice(null);
      saveNoticeHideTimeoutRef.current = null;
    }, durationMs);
  };

  useEffect(() => {
    return () => {
      if (saveNoticeHideTimeoutRef.current != null) {
        window.clearTimeout(saveNoticeHideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (blueprintId) return;
    api<any>(`/api/projects/${projectId}/`)
      .then((proj) => {
        const mkts = Array.isArray(proj.markets) ? proj.markets : [];
        setProjectMarkets(mkts);
        setProjectDisplayName(proj.name || '');
        if (workspaceMode && !blueprintId) {
          setName(proj.name || '');
          setDescription(proj.opportunity_space || proj.description || '');
          if (mkts[0] && !market.trim()) {
            setMarket(mkts[0]);
          }
          setStep(2);
          setInnovationPlatformLocked(true);
        }
      })
      .catch(() => setProjectMarkets([]));
  }, [projectId, workspaceMode, blueprintId, market]);

  // When the workspace drawer saves data without changing the blueprint ID,
  // just refresh the blueprint fields and methodology flags.
  const prevWorkspaceRefreshKeyRef = useRef(0);
  useEffect(() => {
    if (workspaceRefreshKey === prevWorkspaceRefreshKeyRef.current) return;
    prevWorkspaceRefreshKeyRef.current = workspaceRefreshKey;
    if (!createdBlueprint?.id) return;

    const bpId = createdBlueprint.id;
    const tabId = activeRecipeTabIdRef.current;
    void queryClient
      .fetchQuery({
        queryKey: queryKeys.workspace(projectId, bpId, tabId),
        queryFn: () => fetchWorkspace(projectId, { blueprintId: bpId, tabId }),
        staleTime: 0,
      })
      .then((data) => {
        if (!data.blueprint) return;
        const bp = asBlueprint(data.blueprint);
        setCreatedBlueprint(bp);
        setJob(bp.job_to_be_done || '');
        setJobLocked(Boolean(bp.job_to_be_done_locked));
        setTension(bp.tension || '');
        setTensionLocked(Boolean(bp.tension_locked));
        setMethodologyFlags(
          Array.isArray(data.methodology_flags) ? data.methodology_flags : []
        );
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceRefreshKey]);

  useEffect(() => {
    if (!blueprintId) return;

    let cancelled = false;
    setError(null);
    setAttributes([]);
    setAttributeOptions([]);
    setRecipeCardTabs([]);
    setActiveRecipeTabId(null);
    setRecipeCardBrands([]);
    setRecipeCardFormats([]);
    recipeAttributesHydratedRef.current = null;

    (async () => {
      try {
        const data = await queryClient.fetchQuery(
          workspaceQueryOptions(projectId, blueprintId)
        );
        if (cancelled) return;

        const bp = applyWorkspacePayload(data);
        if (!bp) {
          setError('Failed to load blueprint');
          return;
        }

        if (!Array.isArray(data.attributes) || data.attributes.length === 0) {
          await initializeDefaultRecipeAttributes(bp.id);
        }

        if (cancelled) return;
        setInnovationPlatformLocked(true);
        if (workspaceMode && !recipeFocusRef.current) {
          const nextStep = await computeStepForTab(
            bp.id,
            activeRecipeTabIdRef.current
          );
          if (!cancelled) setStep(nextStep);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || 'Failed to load blueprint');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blueprintId, workspaceMode, projectId]);

  useEffect(() => {
    recipeFocusAppliedRef.current = null;
    setConceptArtifactId(null);
    setShowAllMethodologyNotes(false);
    setRecipeCardTabs([]);
    setActiveRecipeTabId(null);
  }, [blueprintId]);

  useEffect(() => {
    if (!recipeFocus || !blueprintId || createdBlueprint?.id !== blueprintId) return;

    const focusKey = JSON.stringify({ blueprintId, ...recipeFocus });
    if (recipeFocusAppliedRef.current === focusKey) return;

    const applyRecipeFocus = async () => {
      setStep(recipeFocus.step);
      if (recipeFocus.recipePageView) {
        setRecipePageView(recipeFocus.recipePageView);
      }

      if (recipeFocus.step >= 3) {
        let targetTabId: number | null | undefined = undefined;
        let preferredCombinationId: number | null | undefined = undefined;
        if (recipeFocus.combinationId != null) {
          const allCombos = await api<RecipeCombination[]>(
            `/api/blueprints/${blueprintId}/recipe-combinations/`
          );
          const targetCombo = Array.isArray(allCombos)
            ? allCombos.find((c) => c.id === recipeFocus.combinationId)
            : undefined;
          targetTabId = targetCombo?.tab ?? null;
          preferredCombinationId = recipeFocus.combinationId;
        }
        await bootstrapFilledRecipeCard(blueprintId, targetTabId, preferredCombinationId);
      }

      if (recipeFocus.artifactId != null) {
        setConceptArtifactId(recipeFocus.artifactId);
      }

      recipeFocusAppliedRef.current = focusKey;
      onRecipeFocusConsumed?.();
    };

    void applyRecipeFocus();
  }, [recipeFocus, blueprintId, createdBlueprint?.id, onRecipeFocusConsumed]);

  useEffect(() => {
    if (!createdBlueprint?.id || !market.trim()) {
      setJtbdStatements([]);
      setJtbdGenerationAllowed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api<JtbdLatestResponse>(`/api/blueprints/${createdBlueprint.id}/jtbd-latest/`);
        if (cancelled) return;
        const stmts = Array.isArray(res.jtbd_statements) ? res.jtbd_statements : [];
        setJtbdStatements(stmts);
        setJtbdGenerationAllowed(res.generation_allowed !== false);
      } catch {
        if (!cancelled) {
          setJtbdStatements([]);
          setJtbdGenerationAllowed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [createdBlueprint?.id, market]);

  useEffect(() => {
    if (!createdBlueprint?.id) return;
    setJobLocked(Boolean(createdBlueprint.job_to_be_done_locked));
    setTensionLocked(Boolean(createdBlueprint.tension_locked));
  }, [
    createdBlueprint?.id,
    createdBlueprint?.job_to_be_done_locked,
    createdBlueprint?.tension_locked,
  ]);

  const fetchTensionsLatest = async (blueprintId: number) => {
    try {
      const res = await api<TensionsLatestResponse>(`/api/blueprints/${blueprintId}/tensions-latest/`);
      const list = Array.isArray(res.tensions) ? res.tensions : [];
      setTensionStatements(list);
      setTensionsGenerationAllowed(res.generation_allowed !== false);
    } catch {
      setTensionStatements([]);
      setTensionsGenerationAllowed(true);
    }
  };

  const fetchBenefitsLatest = async (blueprintId: number) => {
    try {
      const res = await api<BenefitsLatestResponse>(`/api/blueprints/${blueprintId}/benefits-latest/`);
      const list = Array.isArray(res.benefits) ? res.benefits : [];
      setBenefitStatements(list);
      setBenefitsGenerationAllowed(res.generation_allowed !== false);
    } catch {
      setBenefitStatements([]);
      setBenefitsGenerationAllowed(true);
    }
  };

  useEffect(() => {
    if (!createdBlueprint?.id || !jobLocked || !job.trim()) {
      setTensionStatements([]);
      setTensionsGenerationAllowed(true);
      return;
    }
    void fetchTensionsLatest(createdBlueprint.id);
  }, [createdBlueprint?.id, job, jobLocked]);

  useEffect(() => {
    if (!createdBlueprint?.id || !tensionLocked || !job.trim() || !tension.trim()) {
      setBenefitStatements([]);
      setBenefitsGenerationAllowed(true);
      return;
    }
    void fetchBenefitsLatest(createdBlueprint.id);
  }, [createdBlueprint?.id, job, tension, tensionLocked]);

  const activeRecipeTabIdRef = useRef<number | null>(null);
  const recipeAttributesHydratedRef = useRef<number | null>(null);
  const initDefaultsInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (step !== 2 || !blueprintId) return;
    if (createdBlueprint?.id !== blueprintId) return;
    if (recipeAttributesHydratedRef.current === blueprintId) return;
    loadRecipeAttributes(blueprintId).catch(() => {});
  }, [step, blueprintId, createdBlueprint?.id]);

  useEffect(() => {
    if (step !== 3 || !createdBlueprint?.id) return;
    if (recipeFocusRef.current && recipeFocusRef.current.step >= 3) return;
    // Tab switches load the filled card explicitly; skip to avoid a double load.
    if (recipeTabsBusy) return;
    void bootstrapFilledRecipeCard(createdBlueprint.id);
  }, [step, createdBlueprint?.id]);

  const bootstrapFilledRecipeCard = async (
    bpId: number,
    preferredTabId?: number | null,
    preferredCombinationId?: number | null,
  ) => {
    await loadRecipeCardCatalog(bpId);
    const tabs = await api<RecipeCardTabItem[]>(`/api/blueprints/${bpId}/recipe-card-tabs/`);
    const nextTabs = Array.isArray(tabs) ? tabs : [];
    setRecipeCardTabs(nextTabs);
    await ensureDefaultCombinationsForBlueprint(bpId);
    const preferred = preferredTabId ?? activeRecipeTabIdRef.current;
    let tabId =
      preferred && nextTabs.some((tab) => tab.id === preferred)
        ? preferred
        : nextTabs[0]?.id ?? null;
    const latestTabId = activeRecipeTabIdRef.current;
    if (latestTabId && nextTabs.some((tab) => tab.id === latestTabId)) {
      tabId = latestTabId;
    }
    if (tabId) {
      activeRecipeTabIdRef.current = tabId;
      setActiveRecipeTabId(tabId);
      await loadTabAttributes(bpId, tabId);
      const combos = await loadRecipeCombinations(bpId, tabId);
      if (combos.length > 0) {
        const activeCombo =
          (preferredCombinationId != null
            ? combos.find((combo) => combo.id === preferredCombinationId)
            : null) ??
          combos.find((combo) => combo.id === activeCombinationId) ??
          combos[combos.length - 1];
        setActiveCombinationId(activeCombo.id);
        await loadCombinationWorkspace(bpId, activeCombo.id, {
          reloadAttributes: false,
          showOptionsLoading: true,
        });
        const freshOptions =
          combinationOptionsCacheRef.current.get(activeCombo.id) ?? [];
        const nextSelections = autoSelectSingleOptions(
          combinationSelectionsFrom(
            activeCombo,
            freshOptions,
            isRajaProject
          ),
          freshOptions,
          attributesRef.current.map((attr) => attr.id),
        );
        setSelectedOptionByAttribute(nextSelections);
        setActiveOutputVersionByCombination((prev) => {
          if (prev[activeCombo.id] && Object.keys(prev[activeCombo.id]).length > 0) {
            return prev;
          }
          const inferred = inferOutputVersionsFromSelections(
            nextSelections,
            attributeOutputHistory
          );
          if (Object.keys(inferred).length === 0) return prev;
          return { ...prev, [activeCombo.id]: inferred };
        });
      } else {
        setActiveCombinationId(null);
        setSelectedOptionByAttribute({});
      }
    } else {
      setSavedCombinations([]);
      setActiveCombinationId(null);
    }
  };

  useEffect(() => {
    if (isLoadingOptions || isLoadingAttributes) return;
    setSelectedOptionByAttribute((prev) => {
      const next: SelectedOptionsByAttribute = step === 3 ? { ...prev } : {};
      const attributeIds = attributes.map((attr) => attr.id);
      attributes.forEach((attr) => {
        const optionsForAttr = attributeOptions.filter((opt) => opt.attribute_set === attr.id);
        const previousSelected = prev[attr.id] || [];
        const stillValid = previousSelected.filter((optionId) =>
          optionsForAttr.some((opt) => opt.id === optionId)
        );
        if (stillValid.length > 0) {
          next[attr.id] = stillValid;
          return;
        }
        if (optionsForAttr.length === 1) {
          next[attr.id] = [optionsForAttr[0].id];
          return;
        }
        if (step === 3) {
          delete next[attr.id];
        }
      });
      const withSingles = autoSelectSingleOptions(next, attributeOptions, attributeIds);
      return selectionMapsEqual(prev, withSingles) ? prev : withSingles;
    });
  }, [attributes, attributeOptions, step, isLoadingOptions, isLoadingAttributes]);

  const recipeOptionsUrl = (blueprintId: number, combinationId?: number | null) => {
    const base = `/api/blueprints/${blueprintId}/recipe-attribute-options/`;
    return combinationId ? `${base}?combination_id=${combinationId}` : base;
  };

  // Decide builder (step 2) vs filled (step 3) for a single recipe card tab.
  // Prefer persisted tab.phase === 'options'. Never trust 'configuring' alone —
  // marketplace/AI paths can create options without flipping phase.
  const computeStepForTab = async (
    blueprintId: number,
    tabId: number | null
  ): Promise<2 | 3> => {
    if (!tabId) return 2;
    const known = recipeCardTabs.find((t) => t.id === tabId);
    if (known?.phase === 'options') return 3;
    try {
      const combos = await api<RecipeCombination[]>(
        `/api/blueprints/${blueprintId}/recipe-combinations/?tab_id=${tabId}`
      );
      const list = Array.isArray(combos) ? combos : [];
      if (list.length === 0) return 2;
      const comboId = list[list.length - 1].id;
      const options = await api<RecipeAttributeOption[]>(
        recipeOptionsUrl(blueprintId, comboId)
      );
      return Array.isArray(options) && options.length > 0 ? 3 : 2;
    } catch {
      return 2;
    }
  };

  const loadMethodologyFlags = async (bpId: number, tabId?: number | null) => {
    const fetchFlags = (query: string) =>
      api<MethodologyFlag[]>(`/api/blueprints/${bpId}/recipe-methodology-flags/${query}`);

    try {
      const query = tabId ? `?tab_id=${tabId}` : '';
      const flags = await fetchFlags(query);
      const flagsArray = Array.isArray(flags) ? flags : [];
      setMethodologyFlags(flagsArray);
    } catch (err) {
      console.warn('[methodology-flags] fetch failed, bpId=%d tabId=%s', bpId, tabId, err);
      if (tabId) {
        try {
          const flags = await fetchFlags('');
          setMethodologyFlags(Array.isArray(flags) ? flags : []);
          return;
        } catch (fallbackErr) {
          console.warn('[methodology-flags] fallback fetch also failed', fallbackErr);
        }
      }
      setMethodologyFlags([]);
    }
  };

  const loadRecipeCardCatalog = async (bpId: number) => {
    const [brands, formats] = await Promise.all([
      api<RecipeCardBrandOption[]>(`/api/blueprints/${bpId}/recipe-card-brands/`),
      api<RecipeCardFormatOption[]>(`/api/blueprints/${bpId}/recipe-card-formats/`),
    ]);
    setRecipeCardBrands(Array.isArray(brands) ? brands : []);
    setRecipeCardFormats(Array.isArray(formats) ? formats : []);
  };

  const loadRecipeCardTabs = async (bpId: number, preferredTabId?: number | null) => {
    const tabs = await api<RecipeCardTabItem[]>(`/api/blueprints/${bpId}/recipe-card-tabs/`);
    const nextTabs = Array.isArray(tabs) ? tabs : [];
    setRecipeCardTabs(nextTabs);
    const preferred = preferredTabId ?? activeRecipeTabIdRef.current;
    const nextActive =
      preferred && nextTabs.some((tab) => tab.id === preferred)
        ? preferred
        : nextTabs[0]?.id ?? null;
    activeRecipeTabIdRef.current = nextActive;
    setActiveRecipeTabId(nextActive);
    if (nextActive) {
      await loadTabAttributes(bpId, nextActive);
    }
    return nextTabs;
  };

  const loadTabAttributes = async (bpId: number, tabId: number) => {
    setIsLoadingAttributes(true);
    try {
      const data = await api<RecipeAttributeSet[]>(
        `/api/blueprints/${bpId}/recipe-card-tabs/${tabId}/attributes/`
      );
      const next = Array.isArray(data) ? data : [];
      setAttributes(next);
      syncLastSavedAttributeFields(next);
      recipeAttributesHydratedRef.current = bpId;
      void loadMethodologyFlags(bpId, tabId);
    } finally {
      setIsLoadingAttributes(false);
    }
  };

  const loadRecipeAttributes = async (blueprintId: number) => {
    setIsLoadingAttributes(true);
    try {
      await loadRecipeCardCatalog(blueprintId);
      await loadRecipeCardTabs(blueprintId);
    } finally {
      setIsLoadingAttributes(false);
    }
  };

  const initializeDefaultRecipeAttributes = async (blueprintId: number) => {
    if (initDefaultsInFlightRef.current) {
      await initDefaultsInFlightRef.current;
      return;
    }

    const run = (async () => {
      await api(`/api/blueprints/${blueprintId}/recipe-attributes/init-defaults/`, { method: 'POST' });
      try {
        await loadRecipeAttributes(blueprintId);
      } catch (e: any) {
        setError(
          e.message ||
            'Defaults were saved but the recipe list failed to load. Try refreshing the page or opening this blueprint again.'
        );
      }
    })();

    initDefaultsInFlightRef.current = run;
    try {
      await run;
    } finally {
      initDefaultsInFlightRef.current = null;
    }
  };

  const loadAllOptions = async (
    blueprintId: number,
    combinationId?: number | null,
    { showLoading = true, writeCache = true }: { showLoading?: boolean; writeCache?: boolean } = {}
  ) => {
    if (combinationId != null) {
      const cached = combinationOptionsCacheRef.current.get(combinationId);
      if (cached) {
        setAttributeOptions(cached);
        return cached;
      }
    }

    if (showLoading) setIsLoadingOptions(true);
    try {
      const data = await api<RecipeAttributeOption[]>(recipeOptionsUrl(blueprintId, combinationId));
      const next = Array.isArray(data) ? data : [];
      setAttributeOptions(next);
      if (writeCache && combinationId != null) {
        combinationOptionsCacheRef.current.set(combinationId, next);
      }
      return next;
    } finally {
      if (showLoading) setIsLoadingOptions(false);
    }
  };

  const prefetchCombinationOptions = (blueprintId: number, combinationIds: number[]) => {
    void Promise.all(
      combinationIds.map(async (id) => {
        if (combinationOptionsCacheRef.current.has(id)) return;
        try {
          const data = await api<RecipeAttributeOption[]>(recipeOptionsUrl(blueprintId, id));
          combinationOptionsCacheRef.current.set(id, Array.isArray(data) ? data : []);
        } catch {
          // Prefetch failures are non-blocking; tab switch will fetch on demand.
        }
      })
    );
  };

  const invalidateCombinationOptionsCache = (combinationId?: number) => {
    if (combinationId != null) {
      combinationOptionsCacheRef.current.delete(combinationId);
      return;
    }
    combinationOptionsCacheRef.current.clear();
  };

  useEffect(() => {
    if (step !== 3 || !activeCombinationId || isLoadingOptions || isLoadingAttributes) return;

    const combo = savedCombinations.find((c) => c.id === activeCombinationId);
    if (!combo) return;

    const fromCombo = combinationSelectionsFrom(combo, attributeOptions, isRajaProject);
    const next = autoSelectSingleOptions(
      fromCombo,
      attributeOptions,
      attributes.map((attr) => attr.id),
    );
    setSelectedOptionByAttribute((prev) => (selectionMapsEqual(prev, next) ? prev : next));
  }, [
    step,
    activeCombinationId,
    savedCombinations,
    attributeOptions,
    isLoadingOptions,
    isLoadingAttributes,
    isRajaProject,
    attributes,
  ]);

  useEffect(() => {
    if (step !== 3 || !activeCombinationId || !canSaveBlueprint || isLoadingOptions || isLoadingAttributes) {
      return;
    }

    const activeDraft = savedCombinations.find((c) => c.id === activeCombinationId);
    if (!activeDraft) return;

    const fromCombo = combinationSelectionsFrom(activeDraft, attributeOptions, isRajaProject);
    if (selectionMapsEqual(selectedOptionByAttribute, fromCombo)) return;

    if (combinationAutosaveTimeoutRef.current != null) {
      window.clearTimeout(combinationAutosaveTimeoutRef.current);
    }
    combinationAutosaveTimeoutRef.current = window.setTimeout(() => {
      void persistCurrentCombinationSelections();
      combinationAutosaveTimeoutRef.current = null;
    }, 350);

    return () => {
      if (combinationAutosaveTimeoutRef.current != null) {
        window.clearTimeout(combinationAutosaveTimeoutRef.current);
        combinationAutosaveTimeoutRef.current = null;
      }
    };
  }, [
    step,
    activeCombinationId,
    canSaveBlueprint,
    isLoadingOptions,
    isLoadingAttributes,
    selectedOptionByAttribute,
    savedCombinations,
  ]);

  const loadCombinationWorkspace = async (
    blueprintId: number,
    combinationId: number | null,
    { reloadAttributes = false, showOptionsLoading = true }: { reloadAttributes?: boolean; showOptionsLoading?: boolean } = {}
  ) => {
    const tasks: Promise<unknown>[] = [
      loadAllOptions(blueprintId, combinationId, { showLoading: showOptionsLoading }),
    ];
    if (reloadAttributes) {
      tasks.push(loadRecipeAttributes(blueprintId));
    }
    await Promise.all(tasks);
  };

  const loadRecipeCombinations = async (blueprintId: number, tabId?: number | null) => {
    combinationOptionsCacheRef.current.clear();
    const query = tabId ? `?tab_id=${tabId}` : '';
    const data = await api<RecipeCombination[]>(
      `/api/blueprints/${blueprintId}/recipe-combinations/${query}`
    );
    const combos = sortCombinationsOldestFirst(Array.isArray(data) ? data : []);
    setSavedCombinations(combos);

    let nextId: number | null = null;
    setActiveCombinationId((current) => {
      nextId =
        current && combos.some((c) => c.id === current)
          ? current
          : combos[combos.length - 1]?.id ?? null;
      return nextId;
    });

    await loadCombinationWorkspace(blueprintId, nextId, { reloadAttributes: false });
    if (nextId != null) {
      prefetchCombinationOptions(
        blueprintId,
        combos.map((c) => c.id).filter((id) => id !== nextId)
      );
    }
    return combos;
  };

  const runRecipeAiGeneration = async () => {
    if (!createdBlueprint?.id) return;
    if (!canSaveBlueprint) {
      setError('You do not have permission to run AI recipe generation on this blueprint.');
      return;
    }
    setError(null);
    setIsRecipeAiGenerating(true);
    try {
      await flushAllAttributeAutosaves();
      await syncAllTabsDetailsAsUserOptions(createdBlueprint.id);
    } catch (e: any) {
      setError(e.message || 'Failed to sync details into options');
      setIsRecipeAiGenerating(false);
      return;
    }
    try {
      const res = await api<{
        skipped?: boolean;
        message?: string;
        options_created?: number;
        attributes_requested?: number;
      }>(`/api/blueprints/${createdBlueprint.id}/recipe-generate-ai/`, {
        method: 'POST',
        body: JSON.stringify({ options_per_level: 3 }),
      });
      setShowGenerationSummaryModal(false);
      if (res.skipped) {
        showTransientSaveNotice(
          res.message || 'Recipe options are already up to date.',
          5000
        );
      } else {
        requestNotificationsRefresh();
      }
      setStep(3);
      invalidateCombinationOptionsCache();
      await ensureDefaultCombinationsForBlueprint(createdBlueprint.id);
      await bootstrapFilledRecipeCard(createdBlueprint.id);
    } catch (e: any) {
      setError(e.message || 'Recipe AI generation failed');
    } finally {
      setIsRecipeAiGenerating(false);
    }
  };

  const applyDetailsOptionsForAttribute = async (
    blueprintId: number,
    attrId: number,
    desiredOptions: string[],
    options: RecipeAttributeOption[],
    combinationId: number | null = null
  ) => {
    // Non-exploratory detail options are always card-scoped (combination NULL).
    // The combinationId arg is ignored for create/update; kept for call-site compat.
    void combinationId;
    const attrOptions = options.filter((opt) => opt.attribute_set === attrId);
    const isDetailOption = (opt: RecipeAttributeOption) =>
      opt.generation_source === 'user' && (opt.option_notes || '') === DETAILS_OPTION_NOTE;
    // Shared detail options for this attribute (combination NULL).
    const detailOptions = attrOptions.filter(
      (opt) => isDetailOption(opt) && (opt.combination ?? null) === null
    );
    // Legacy combo-scoped detail options should be cleared so they do not
    // masquerade as exploratory / combo-only inputs.
    const legacyComboDetailOptions = attrOptions.filter(
      (opt) => isDetailOption(opt) && (opt.combination ?? null) !== null
    );

    const deleteOption = (opt: RecipeAttributeOption) =>
      api(`/api/blueprints/${blueprintId}/recipe-attribute-options/${opt.id}/`, {
        method: 'DELETE',
      });

    if (desiredOptions.length === 0) {
      for (const opt of [...detailOptions, ...legacyComboDetailOptions]) {
        await deleteOption(opt);
      }
      return;
    }

    const matchedOptionIds = new Set<number>();

    for (let index = 0; index < desiredOptions.length; index += 1) {
      const optionText = desiredOptions[index];
      const existing = detailOptions.find(
        (opt) =>
          !matchedOptionIds.has(opt.id) &&
          (opt.option_text || '').trim().toLowerCase() === optionText.toLowerCase()
      );

      if (existing) {
        matchedOptionIds.add(existing.id);
        if (existing.display_order !== index || (existing.option_text || '').trim() !== optionText) {
          await api(`/api/blueprints/${blueprintId}/recipe-attribute-options/${existing.id}/`, {
            method: 'PATCH',
            body: JSON.stringify({
              option_text: optionText,
              display_order: index,
            }),
          });
        }
        continue;
      }

      await api(`/api/blueprints/${blueprintId}/recipe-attribute-options/`, {
        method: 'POST',
        body: JSON.stringify({
          attribute_set: attrId,
          option_text: optionText,
          option_notes: DETAILS_OPTION_NOTE,
          generation_source: 'user',
          is_user_added: true,
          display_order: attrOptions.length + index,
        }),
      });
    }

    for (const opt of detailOptions) {
      if (!matchedOptionIds.has(opt.id)) {
        await deleteOption(opt);
      }
    }
    for (const opt of legacyComboDetailOptions) {
      await deleteOption(opt);
    }
  };

  const syncDetailsAsUserOptions = async (blueprintId: number) => {
    const existingOptions = await api<RecipeAttributeOption[]>(
      `/api/blueprints/${blueprintId}/recipe-attribute-options/`
    );
    const options = Array.isArray(existingOptions) ? existingOptions : [];

    for (const attr of attributesRef.current) {
      const desiredOptions = parseDetailOptions(attr.details_text || '');
      await applyDetailsOptionsForAttribute(blueprintId, attr.id, desiredOptions, options);
    }
  };

  const syncAllTabsDetailsAsUserOptions = async (blueprintId: number) => {
    const tabs =
      recipeCardTabs.length > 0
        ? recipeCardTabs
        : await api<RecipeCardTabItem[]>(`/api/blueprints/${blueprintId}/recipe-card-tabs/`);

    const tabList = Array.isArray(tabs) ? tabs : [];
    if (tabList.length === 0) return;

    // Details become card-scoped shared options (combination NULL), so every
    // combination on that recipe card can use them. Exploratory inputs stay
    // combination-scoped via the exploratory modal.
    await ensureDefaultCombinationsForBlueprint(blueprintId);

    for (const tab of tabList) {
      const attrs =
        tab.id === activeRecipeTabIdRef.current
          ? attributesRef.current
          : await api<RecipeAttributeSet[]>(
              `/api/blueprints/${blueprintId}/recipe-card-tabs/${tab.id}/attributes/`
            );
      const attrList = Array.isArray(attrs) ? attrs : [];
      if (attrList.length === 0) continue;

      const existingOptions = await api<RecipeAttributeOption[]>(
        `/api/blueprints/${blueprintId}/recipe-attribute-options/?tab_id=${tab.id}`
      );
      const options = Array.isArray(existingOptions) ? existingOptions : [];

      for (const attr of attrList) {
        const desiredOptions = parseDetailOptions(attr.details_text || '');
        await applyDetailsOptionsForAttribute(
          blueprintId,
          attr.id,
          desiredOptions,
          options,
          null
        );
      }
    }
  };

  const ensureDefaultCombinationsForBlueprint = async (blueprintId: number) => {
    await api(`/api/blueprints/${blueprintId}/recipe-combinations/ensure-defaults/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  };

  const canGoNext = useMemo(() => {
    if (step === 1) {
      return Boolean(
        market.trim() &&
        job.trim() &&
        tension.trim() &&
        benefit.trim() &&
        permission.trim()
      );
    }
    if (step === 2) return true;
    return false;
  }, [step, market, job, tension, benefit, permission]);

  /** Step 1 continue runs POST/PATCH blueprint then init-defaults (POST); both need the right caps. */
  const canAdvanceFromStep1 = useMemo(() => {
    if (!canGoNext) return false;
    if (!createdBlueprint?.id) {
      return canCreateBlueprint && canSaveBlueprint;
    }
    return canSaveBlueprint;
  }, [canGoNext, createdBlueprint?.id, canCreateBlueprint, canSaveBlueprint]);

  const attributeHasUserDetails = (attr: RecipeAttributeSet) => Boolean((attr.details_text || '').trim());

  const attributesForGeneration = useMemo(
    () => attributes.filter((attr) => !attributeHasUserDetails(attr)),
    [attributes]
  );

  const attributesSkippedDueToDetails = useMemo(
    () => attributes.filter((attr) => attributeHasUserDetails(attr)),
    [attributes]
  );

  const handleStep2PrimaryAction = () => {
    setShowRecipeCardBuilderModal(true);
    setRecipeCardBuilderProductsLoading(true);
    void queryClient
      .ensureQueryData(marketplaceProductsQuery('recipe_card_builder'))
      .then(setRecipeCardBuilderProducts)
      .finally(() => setRecipeCardBuilderProductsLoading(false));
  };

  const handleRecipeCardBuilderProduct = (product: MarketplaceProduct) => {
    setShowRecipeCardBuilderModal(false);
    if (product.service_type === 'DIY') {
      setGenerationMode(null);
      void handleNext(null);
      return;
    }
    onNavigateToMarketplaceProduct?.(product.product_category, product.id);
  };

  const openProductIdeaValidatorModal = () => {
    setShowProductIdeaValidatorModal(true);
    setProductIdeaValidatorProductsLoading(true);
    void queryClient
      .ensureQueryData(marketplaceProductsQuery('product_idea_validator'))
      .then(setProductIdeaValidatorProducts)
      .finally(() => setProductIdeaValidatorProductsLoading(false));
  };

  const handleProductIdeaValidatorProduct = (product: MarketplaceProduct) => {
    setShowProductIdeaValidatorModal(false);
    onNavigateToMarketplaceProduct?.(product.product_category, product.id);
  };

  const openRecipeCardValidatorModal = () => {
    setShowRecipeCardValidatorModal(true);
    setRecipeCardValidatorProductsLoading(true);
    void queryClient
      .ensureQueryData(marketplaceProductsQuery('recipe_card_validator'))
      .then(setRecipeCardValidatorProducts)
      .finally(() => setRecipeCardValidatorProductsLoading(false));
  };

  const handleRecipeCardValidatorProduct = (product: MarketplaceProduct) => {
    setShowRecipeCardValidatorModal(false);
    onNavigateToMarketplaceProduct?.(product.product_category, product.id);
  };

  const openProductIdeaGeneratorModal = () => {
    setShowProductIdeaGeneratorModal(true);
    setProductIdeaGeneratorProductsLoading(true);
    void queryClient
      .ensureQueryData(marketplaceProductsQuery('product_idea_generator'))
      .then(setProductIdeaGeneratorProducts)
      .finally(() => setProductIdeaGeneratorProductsLoading(false));
  };

  const handleProductIdeaGeneratorProduct = (product: MarketplaceProduct) => {
    setShowProductIdeaGeneratorModal(false);
    onNavigateToMarketplaceProduct?.(product.product_category, product.id);
  };

  const handleNext = async (modeOverride?: 'ai' | 'crowd' | null) => {
    if (step >= TOTAL_STEPS) return;
    if (step === 1) {
      if (!canAdvanceFromStep1) return;
    } else if (!canGoNext) {
      return;
    }
    if (step === 2 && !canSaveBlueprint) {
      setError('You do not have permission to save changes to this blueprint.');
      return;
    }

    const activeGenerationMode = modeOverride !== undefined ? modeOverride : generationMode;

    // Step 1 -> Step 2: persist blueprint first; seed defaults only on first create (POST)
    if (step === 1) {
      setStep1ContinueBusy(true);
      setError(null);
      try {
        const saved = await saveBlueprint();
        if (!saved) return;
        setInnovationPlatformLocked(true);
        if (saved.id) {
          const attrs = await api<RecipeAttributeSet[]>(
            `/api/blueprints/${saved.id}/recipe-attributes/`
          );
          if (Array.isArray(attrs) && attrs.length > 0) {
            await loadRecipeCardCatalog(saved.id);
            await loadRecipeCardTabs(saved.id);
          } else {
            await initializeDefaultRecipeAttributes(saved.id);
          }
        }
        setStep(2);
      } catch (e: any) {
        setError(e.message || 'Could not continue to the recipe step. Check the network tab for failed API calls.');
      } finally {
        setStep1ContinueBusy(false);
      }
      return;
    }

    if (step === 2) {
      if (!createdBlueprint?.id) {
        setError('Blueprint is not ready yet. Please try again.');
        return;
      }
      try {
        await flushAllAttributeAutosaves();
        await syncAllTabsDetailsAsUserOptions(createdBlueprint.id);
      } catch (e: any) {
        setError(e.message || 'Failed to sync details into options');
        return;
      }
    }

    if (step === 2 && (activeGenerationMode === 'ai' || activeGenerationMode === 'crowd')) {
      if (modeOverride !== undefined) {
        setGenerationMode(modeOverride);
      }
      setShowGenerationSummaryModal(true);
      return;
    }

    if (step === 2 && modeOverride !== undefined) {
      setGenerationMode(modeOverride);
    }

    setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step <= 1) return;
    void (async () => {
      if (step === 2) {
        await flushAllAttributeAutosaves();
      }
      setStep((s) => s - 1);
    })();
  };

  const handleHeaderBack = () => {
    if (workspaceMode && step >= 2) {
      onBackToWorkspace?.();
      return;
    }
    if (step > 1) {
      handleBack();
      return;
    }
    onClose();
  };

  const handleGenerateChoice = (mode: 'ai' | 'crowd' | null) => {
    setGenerationMode(mode);
    void handleNext(mode);
  };

  const persistBlueprintOnMarketSelect = async (selectedMarket: string): Promise<boolean> => {
    const marketValue = selectedMarket.trim();
    if (!marketValue) return false;

    if (!createdBlueprint?.id) {
      if (!canCreateBlueprint) {
        setError('You do not have permission to create blueprints.');
        return false;
      }
    } else if (!canSaveBlueprint) {
      setError('You do not have permission to save blueprint changes.');
      return false;
    }

    setIsMarketAutosaving(true);
    setError(null);
    try {
      if (createdBlueprint?.id) {
        const updated = await api<Blueprint>(`/api/blueprints/${createdBlueprint.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({
            name,
            description,
          }),
        });
        setCreatedBlueprint(updated);
        onSuccess?.(updated);
        touchWorkspaceCache();
        return true;
      }

      const created = await api<Blueprint>('/api/blueprints/', {
        method: 'POST',
        body: JSON.stringify({
          project: projectId,
          name,
          description,
          market: marketValue,
          uses_new_workflow: true,
        }),
      });
      setCreatedBlueprint(created);
      onSuccess?.(created);
      touchWorkspaceCache();
      return true;
    } catch (e: any) {
      setError(e.message || 'Failed to save blueprint');
      return false;
    } finally {
      setIsMarketAutosaving(false);
    }
  };

  const handleMarketChange = async (nextMarket: string) => {
    if (marketLocked) return;
    setMarket(nextMarket);
    if (!nextMarket.trim()) return;

    setMarketLocked(true);
    const ok = await persistBlueprintOnMarketSelect(nextMarket);
    if (!ok) {
      setMarketLocked(false);
    }
  };

  const handleAssistClick = async (kind: AssistKind = 'other') => {
    setAssistKind(kind);
    setAssistCheckBusy(true);
    setAssistResearchCompleted(false);
    setAssistModalMessage(null);
    setAssistRequiresJobLock(false);
    setAssistRequiresTensionLock(false);

    if (kind === 'job' && (jtbdStatements.length > 0 || !jtbdGenerationAllowed)) {
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(false);
      if (jtbdStatements.length > 0) {
        setShowJtbdPickerModal(true);
      } else {
        showTransientSaveNotice(
          'Jobs to be done already exist for this project and market — pick one from the list.',
          5000
        );
      }
      return;
    }

    if (kind === 'tension' && (tensionStatements.length > 0 || !tensionsGenerationAllowed)) {
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(false);
      if (tensionStatements.length > 0) {
        setShowTensionPickerModal(true);
      } else {
        showTransientSaveNotice(
          'Tensions already exist for this project, market, and job — pick one from the list.',
          5000
        );
      }
      return;
    }

    if (kind === 'benefit' && (benefitStatements.length > 0 || !benefitsGenerationAllowed)) {
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(false);
      if (benefitStatements.length > 0) {
        setShowBenefitPickerModal(true);
      } else {
        showTransientSaveNotice(
          'Benefits already exist for this project, market, job, and tension — pick one from the list.',
          5000
        );
      }
      return;
    }

    if ((kind === 'tension' || kind === 'benefit') && !jobLocked) {
      setAssistModalMessage('Please lock Job to be done first.');
      setAssistRequiresJobLock(true);
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(true);
      return;
    }

    if (kind === 'benefit' && !tensionLocked) {
      setAssistModalMessage('Please lock Tension first.');
      setAssistRequiresTensionLock(true);
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(true);
      return;
    }

    const blueprintMarket = resolveBlueprintMarket(market, createdBlueprint?.market);
    console.log('[Assist] checking research job status', {
      projectId,
      marketState: market,
      savedBlueprintMarket: createdBlueprint?.market,
      blueprintMarket,
    });

    if (!blueprintMarket) {
      setAssistModalMessage('Please select a market for this blueprint first.');
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(true);
      return;
    }

    const url = `/api/projects/${projectId}/research-job-status/?market=${encodeURIComponent(blueprintMarket)}`;
    try {
      const payload = await api<ResearchJobStatusResponse>(url);
      const ready = isResearchJobCompleted(payload);
      console.log('[Assist] research job status response', {
        requested: { projectId, blueprintMarket, url },
        response: payload,
        matchedCountry: payload?.country,
        statusColumn: payload?.status,
        statusIsCompleted: (payload?.status || '').trim().toLowerCase() === 'completed',
        ready,
      });
      if (payload?.message) {
        setAssistModalMessage(payload.message);
      } else if (!ready && payload?.check_type === 'market_synthesis') {
        setAssistModalMessage('Market synthesis is not ready yet. Please try again later.');
      } else if (!ready) {
        setAssistModalMessage(null);
      }
      setAssistResearchCompleted(ready);
    } catch (err) {
      console.warn('[Assist] research job status request failed', {
        requested: { projectId, blueprintMarket, url },
        error: err,
      });
      setAssistModalMessage('Could not check research status. Try again in a few minutes.');
      setAssistResearchCompleted(false);
    } finally {
      setAssistCheckBusy(false);
      setShowUnderConstructionModal(true);
    }
  };

  const handleJtbdGenerate = async () => {
    if (!createdBlueprint?.id) {
      setError('Save the blueprint with a market before generating jobs to be done.');
      return;
    }
    if (!jtbdGenerationAllowed || jtbdStatements.length > 0) {
      setShowUnderConstructionModal(false);
      showTransientSaveNotice(
        'Jobs to be done already exist for this project and market — pick one from the list.',
        5000
      );
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to generate jobs to be done.');
      return;
    }
    setJtbdGenerateBusy(true);
    setError(null);
    try {
      const res = await api<JtbdGenerateResponse>(`/api/blueprints/${createdBlueprint.id}/jtbd-generate/`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const stmts = Array.isArray(res.jtbd_statements) ? res.jtbd_statements : [];
      setJtbdStatements(stmts);
      setJtbdGenerationAllowed(false);
      setShowUnderConstructionModal(false);
      requestNotificationsRefresh();
    } catch (e: any) {
      const existing = Array.isArray(e?.body?.jtbd_statements) ? e.body.jtbd_statements : [];
      if (existing.length > 0) {
        setJtbdStatements(existing);
        setJtbdGenerationAllowed(false);
        setShowUnderConstructionModal(false);
        showTransientSaveNotice(
          'Jobs to be done already exist for this project and market — pick one from the list.',
          5000
        );
        return;
      }
      const msg = e?.message || 'JTBD generation failed.';
      setError(msg);
      if (e?.body?.request_payload) {
        console.error('[JTBD] outbound request_payload', e.body.request_payload);
      }
      if (e?.body?.pipeline_response) {
        console.error('[JTBD] pipeline_response', e.body.pipeline_response);
      }
    } finally {
      setJtbdGenerateBusy(false);
    }
  };

  const handleLockJobToBeDone = async () => {
    if (!createdBlueprint?.id) {
      setError('Save the blueprint with a market before locking Job to be done.');
      return;
    }
    if (!job.trim()) {
      setError('Job to be done cannot be empty.');
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to save Job to be done.');
      return;
    }
    setJobLockBusy(true);
    setError(null);
    try {
      const updated = await api<Blueprint>(`/api/blueprints/${createdBlueprint.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          job_to_be_done: job,
          job_to_be_done_locked: true,
        }),
      });
      setCreatedBlueprint(updated);
      setJob(updated.job_to_be_done || job);
      setJobLocked(Boolean(updated.job_to_be_done_locked));
      setAssistRequiresJobLock(false);
      await fetchTensionsLatest(updated.id);
      void loadMethodologyFlags(updated.id, activeRecipeTabIdRef.current);
      touchWorkspaceCache();
      showTransientSaveNotice('Job to be done locked and saved.', 3500);
    } catch (e: any) {
      setError(e?.message || 'Failed to lock Job to be done.');
    } finally {
      setJobLockBusy(false);
    }
  };

  const handleLockTension = async () => {
    if (!createdBlueprint?.id) {
      setError('Save the blueprint with a market before locking Tension.');
      return;
    }
    if (!tension.trim()) {
      setError('Tension cannot be empty.');
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to save Tension.');
      return;
    }
    setTensionLockBusy(true);
    setError(null);
    try {
      const updated = await api<Blueprint>(`/api/blueprints/${createdBlueprint.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          tension,
          tension_locked: true,
        }),
      });
      setCreatedBlueprint(updated);
      setTension(updated.tension || tension);
      setTensionLocked(Boolean(updated.tension_locked));
      setAssistRequiresTensionLock(false);
      await fetchBenefitsLatest(updated.id);
      void loadMethodologyFlags(updated.id, activeRecipeTabIdRef.current);
      touchWorkspaceCache();
      showTransientSaveNotice('Tension locked and saved.', 3500);
    } catch (e: any) {
      setError(e?.message || 'Failed to lock Tension.');
    } finally {
      setTensionLockBusy(false);
    }
  };

  const handleTensionsGenerate = async () => {
    if (!createdBlueprint?.id) {
      setError('Save the blueprint with a market before generating tensions.');
      return;
    }
    if (!jobLocked) {
      setError('Please lock Job to be done before generating tensions.');
      return;
    }
    if (!tensionsGenerationAllowed || tensionStatements.length > 0) {
      setShowUnderConstructionModal(false);
      showTransientSaveNotice(
        'Tensions already exist for this project, market, and job — pick one from the list.',
        5000
      );
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to generate tensions.');
      return;
    }
    setTensionGenerateBusy(true);
    setError(null);
    try {
      const res = await api<TensionsGenerateResponse>(`/api/blueprints/${createdBlueprint.id}/tensions-generate/`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const list = Array.isArray(res.tensions) ? res.tensions : [];
      setTensionStatements(list);
      setTensionsGenerationAllowed(false);
      setShowUnderConstructionModal(false);
      requestNotificationsRefresh();
    } catch (e: any) {
      const existing = Array.isArray(e?.body?.tensions) ? e.body.tensions : [];
      if (existing.length > 0) {
        setTensionStatements(existing);
        setTensionsGenerationAllowed(false);
        setShowUnderConstructionModal(false);
        showTransientSaveNotice(
          'Tensions already exist for this project, market, and job — pick one from the list.',
          5000
        );
        return;
      }
      const msg = e?.message || 'Tensions generation failed.';
      setError(msg);
      if (e?.body?.request_payload) {
        console.error('[Tensions] outbound request_payload', e.body.request_payload);
      }
      if (e?.body?.pipeline_response) {
        console.error('[Tensions] pipeline_response', e.body.pipeline_response);
      }
    } finally {
      setTensionGenerateBusy(false);
    }
  };

  const handleBenefitsGenerate = async () => {
    if (!createdBlueprint?.id) {
      setError('Save the blueprint with a market before generating benefits.');
      return;
    }
    if (!jobLocked) {
      setError('Please lock Job to be done before generating benefits.');
      return;
    }
    if (!tensionLocked) {
      setError('Please lock Tension before generating benefits.');
      return;
    }
    if (!benefitsGenerationAllowed || benefitStatements.length > 0) {
      setShowUnderConstructionModal(false);
      showTransientSaveNotice(
        'Benefits already exist for this project, market, job, and tension — pick one from the list.',
        5000
      );
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to generate benefits.');
      return;
    }
    setBenefitGenerateBusy(true);
    setError(null);
    try {
      const res = await api<BenefitsGenerateResponse>(
        `/api/blueprints/${createdBlueprint.id}/benefits-generate/`,
        {
          method: 'POST',
          body: JSON.stringify({
            jtbd: job,
            tension,
          }),
        }
      );
      const list = Array.isArray(res.benefits) ? res.benefits : [];
      setBenefitStatements(list);
      setBenefitsGenerationAllowed(false);
      setShowUnderConstructionModal(false);
      requestNotificationsRefresh();
    } catch (e: any) {
      const existing = Array.isArray(e?.body?.benefits) ? e.body.benefits : [];
      if (existing.length > 0) {
        setBenefitStatements(existing);
        setBenefitsGenerationAllowed(false);
        setShowUnderConstructionModal(false);
        showTransientSaveNotice(
          'Benefits already exist for this project, market, job, and tension — pick one from the list.',
          5000
        );
        return;
      }
      const msg = e?.message || 'Benefits generation failed.';
      setError(msg);
      if (e?.body?.request_payload) {
        console.error('[Benefits] outbound request_payload', e.body.request_payload);
      }
      if (e?.body?.pipeline_response) {
        console.error('[Benefits] pipeline_response', e.body.pipeline_response);
      }
    } finally {
      setBenefitGenerateBusy(false);
    }
  };

  const saveBlueprint = async (): Promise<Blueprint | null> => {
    if (!createdBlueprint?.id && !canCreateBlueprint) {
      setError('You do not have permission to create blueprints.');
      return null;
    }
    if (createdBlueprint?.id && !canSaveBlueprint) {
      setError('You do not have permission to save blueprint changes.');
      return null;
    }
    setError(null);
    setIsSaving(true);
    try {
      // First save creates; later saves update same blueprint (no duplicate blueprints).
      if (createdBlueprint?.id) {
        // Market is set at creation / market-select autosave only; PATCH rejects market changes.
        const updatePayload: any = {
          name,
          description,
          job_to_be_done: job,
          tension,
          benefit,
          permission,
        };
        const updated = await api<Blueprint>(`/api/blueprints/${createdBlueprint.id}/`, {
          method: 'PATCH',
          body: JSON.stringify(updatePayload),
        });
        setCreatedBlueprint(updated);
        onSuccess?.(updated);
        void loadMethodologyFlags(updated.id, activeRecipeTabIdRef.current);
        touchWorkspaceCache();
        return updated;
      }

      const createPayload: any = {
        project: projectId,
        name,
        description,
        market,
        job_to_be_done: job,
        tension,
        benefit,
        permission,
        uses_new_workflow: true,
      };

      const created = await api<Blueprint>('/api/blueprints/', {
        method: 'POST',
        body: JSON.stringify(createPayload),
      });
      setCreatedBlueprint(created);
      onSuccess?.(created);
      void loadMethodologyFlags(created.id, activeRecipeTabIdRef.current);
      touchWorkspaceCache();
      return created;
    } catch (e: any) {
      setError(e.message || 'Failed to save blueprint');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const generateFirstArtifact = async () => {
    setError(null);
    setIsGenerating(true);
    try {
      let blueprint = createdBlueprint;
      if (!blueprint) {
        blueprint = await saveBlueprint();
      }
      if (!blueprint?.id) return;

      const customPromptAdditions = [
        generationMode ? `Generation mode: ${generationMode}` : '',
        targetAudience ? `Target audience: ${targetAudience}` : '',
        occasionContext ? `Occasion/context: ${occasionContext}` : '',
        reasonsToBelieve ? `Reasons to believe: ${reasonsToBelieve}` : '',
        innovationGuardrails ? `Guardrails: ${innovationGuardrails}` : '',
      ]
        .filter(Boolean)
        .join('\n');

      await api(`/api/blueprints/${blueprint.id}/generate_artifact_v2/`, {
        method: 'POST',
        body: JSON.stringify({
          generate_visual: true,
          custom_prompt_additions: customPromptAdditions,
        }),
      });

      const refreshed = await api<Blueprint>(`/api/blueprints/${blueprint.id}/`);
      setCreatedBlueprint(refreshed);
      onSuccess?.(refreshed);
      touchWorkspaceCache();
    } catch (e: any) {
      setError(e.message || 'Failed to generate artifact');
    } finally {
      setIsGenerating(false);
    }
  };

  const updateAttributeValue = (idx: number, value: string) => {
    setAttributes((arr) => {
      const copy = arr.map((a, i) => (i === idx ? { ...a, details_text: value } : a));
      attributesRef.current = copy;
      return copy;
    });
    const attrId = attributesRef.current[idx]?.id;
    if (attrId) scheduleAttributeAutosave(attrId);
  };

  const updateAttributeConstraints = (idx: number, value: string) => {
    setAttributes((arr) => {
      const copy = arr.map((a, i) => (i === idx ? { ...a, constraints_text: value } : a));
      attributesRef.current = copy;
      return copy;
    });
    const attrId = attributesRef.current[idx]?.id;
    if (attrId) scheduleAttributeAutosave(attrId);
  };

  const updateAttributeEvidenced = (idx: number, evidenced: boolean) => {
    setAttributes((arr) => {
      const copy = arr.map((a, i) => (i === idx ? { ...a, has_been_evidenced: evidenced } : a));
      attributesRef.current = copy;
      return copy;
    });
    const attrId = attributesRef.current[idx]?.id;
    if (attrId) scheduleAttributeAutosave(attrId);
  };

  const handleRecipeTabSelect = async (tabId: number) => {
    if (!createdBlueprint?.id || tabId === activeRecipeTabId) return;
    setRecipeTabsBusy(true);
    setError(null);
    try {
      await flushAllAttributeAutosaves();
      activeRecipeTabIdRef.current = tabId;
      setActiveRecipeTabId(tabId);

      // Reset per-attribute workspace state so the previous tab's data never
      // leaks into the newly selected tab.
      setAttributeOptions([]);
      setSelectedOptionByAttribute({});
      setAttributeOutputHistory({});
      setActiveOutputVersionByCombination({});
      setActiveCombinationId(null);
      setSavedCombinations([]);
      combinationOptionsCacheRef.current.clear();

      await loadTabAttributes(createdBlueprint.id, tabId);
      void loadMethodologyFlags(createdBlueprint.id, tabId);

      // Each tab independently shows the builder (no options yet) or the filled
      // view (its own combination has options).
      const nextStep = await computeStepForTab(createdBlueprint.id, tabId);
      setStep(nextStep);
      if (nextStep === 3) {
        await ensureDefaultCombinationsForBlueprint(createdBlueprint.id);
        await bootstrapFilledRecipeCard(createdBlueprint.id, tabId);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to switch tab');
    } finally {
      setRecipeTabsBusy(false);
    }
  };

  const handleCreateRecipeTab = async () => {
    if (!createdBlueprint?.id || !canSaveBlueprint) return;
    setRecipeTabsBusy(true);
    setError(null);
    try {
      await flushAllAttributeAutosaves();
      const created = await api<RecipeCardTabItem>(
        `/api/blueprints/${createdBlueprint.id}/recipe-card-tabs/`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      setRecipeCardTabs((prev) => [...prev, created]);
      activeRecipeTabIdRef.current = created.id;
      setActiveRecipeTabId(created.id);
      // New card always starts in configuring (state 1) with a clean local workspace.
      invalidateCombinationOptionsCache();
      setAttributeOptions([]);
      setSelectedOptionByAttribute({});
      setSavedCombinations([]);
      setActiveCombinationId(null);
      setStep(2);
      setRecipePageView('recipe-card');
      await loadTabAttributes(createdBlueprint.id, created.id);
      await ensureDefaultCombinationsForBlueprint(createdBlueprint.id);
    } catch (e: any) {
      setError(e.message || 'Failed to create tab');
    } finally {
      setRecipeTabsBusy(false);
    }
  };

  const handleRenameRecipeTab = async (tabId: number, tabName: string) => {
    if (!createdBlueprint?.id || !canSaveBlueprint) return;
    try {
      const updated = await api<RecipeCardTabItem>(
        `/api/blueprints/${createdBlueprint.id}/recipe-card-tabs/${tabId}/`,
        { method: 'PATCH', body: JSON.stringify({ tab_name: tabName }) }
      );
      setRecipeCardTabs((prev) => prev.map((tab) => (tab.id === tabId ? updated : tab)));
    } catch (e: any) {
      setError(e.message || 'Failed to rename tab');
    }
  };

  const requestDeleteRecipeTab = (tabId: number) => {
    setPendingDeleteTabId(tabId);
    setShowDeleteTabModal(true);
  };

  const closeDeleteTabModal = () => {
    if (recipeTabsBusy) return;
    setShowDeleteTabModal(false);
    setPendingDeleteTabId(null);
  };

  const confirmDeleteRecipeTab = async () => {
    const tabId = pendingDeleteTabId;
    if (!createdBlueprint?.id || !canSaveBlueprint || tabId == null) return;

    setRecipeTabsBusy(true);
    setError(null);
    try {
      await flushAllAttributeAutosaves();
      await api(`/api/blueprints/${createdBlueprint.id}/recipe-card-tabs/${tabId}/`, {
        method: 'DELETE',
      });
      const remaining = recipeCardTabs.filter((tab) => tab.id !== tabId);
      setRecipeCardTabs(remaining);
      const nextActive = remaining[0]?.id ?? null;
      activeRecipeTabIdRef.current = nextActive;
      setActiveRecipeTabId(nextActive);

      // Clear workspace so deleted card state never sticks on the next tab.
      setAttributeOptions([]);
      setSelectedOptionByAttribute({});
      setAttributeOutputHistory({});
      setActiveOutputVersionByCombination({});
      setActiveCombinationId(null);
      setSavedCombinations([]);
      combinationOptionsCacheRef.current.clear();

      if (nextActive) {
        await loadTabAttributes(createdBlueprint.id, nextActive);
        void loadMethodologyFlags(createdBlueprint.id, nextActive);
        // Recompute phase for the remaining card (do not keep the deleted card's step).
        const nextStep = await computeStepForTab(createdBlueprint.id, nextActive);
        setStep(nextStep);
        if (nextStep === 3) {
          await ensureDefaultCombinationsForBlueprint(createdBlueprint.id);
          await bootstrapFilledRecipeCard(createdBlueprint.id, nextActive);
        }
      } else {
        setAttributes([]);
        setStep(2);
      }
      closeDeleteTabModal();
    } catch (e: any) {
      setError(e.message || 'Failed to delete tab');
    } finally {
      setRecipeTabsBusy(false);
    }
  };

  const handleDeleteRecipeTab = requestDeleteRecipeTab;

  const handleSelectRecipeTabBrand = async (tabId: number, brandId: number) => {
    if (!createdBlueprint?.id || !canSaveBlueprint) return;
    const tab = recipeCardTabs.find((item) => item.id === tabId);
    if (!tab || tab.brand_locked || tab.brand === brandId) return;
    setRecipeTabsBusy(true);
    setError(null);
    try {
      const updated = await api<RecipeCardTabItem>(
        `/api/blueprints/${createdBlueprint.id}/recipe-card-tabs/${tabId}/`,
        { method: 'PATCH', body: JSON.stringify({ brand_id: brandId }) }
      );
      setRecipeCardTabs((prev) => prev.map((item) => (item.id === tabId ? updated : item)));
    } catch (e: any) {
      setError(e.message || 'Failed to set brand');
    } finally {
      setRecipeTabsBusy(false);
    }
  };

  const handleSelectRecipeTabFormat = async (tabId: number, formatId: number) => {
    if (!createdBlueprint?.id || !canSaveBlueprint) return;
    const tab = recipeCardTabs.find((item) => item.id === tabId);
    if (!tab || tab.format_locked || tab.format === formatId) return;
    setRecipeTabsBusy(true);
    setError(null);
    try {
      const updated = await api<RecipeCardTabItem>(
        `/api/blueprints/${createdBlueprint.id}/recipe-card-tabs/${tabId}/`,
        { method: 'PATCH', body: JSON.stringify({ format_id: formatId }) }
      );
      setRecipeCardTabs((prev) => prev.map((item) => (item.id === tabId ? updated : item)));
    } catch (e: any) {
      setError(e.message || 'Failed to set format');
    } finally {
      setRecipeTabsBusy(false);
    }
  };

  const handleCreateRecipeBrand = async (name: string) => {
    if (!createdBlueprint?.id || !canSaveBlueprint) return;
    const created = await api<RecipeCardBrandOption>(
      `/api/blueprints/${createdBlueprint.id}/recipe-card-brands/`,
      { method: 'POST', body: JSON.stringify({ name }) }
    );
    setRecipeCardBrands((prev) => [...prev, created]);
  };

  const handleCreateRecipeFormat = async (name: string) => {
    if (!createdBlueprint?.id || !canSaveBlueprint) return;
    const created = await api<RecipeCardFormatOption>(
      `/api/blueprints/${createdBlueprint.id}/recipe-card-formats/`,
      { method: 'POST', body: JSON.stringify({ name }) }
    );
    setRecipeCardFormats((prev) => [...prev, created]);
  };

  const deleteAttributeSet = async (idx: number) => {
    const attr = attributes[idx];
    const tabId = activeRecipeTabIdRef.current;
    if (!createdBlueprint?.id || !attr?.id) return;
    if (!tabId) {
      setError('Select a recipe card tab before removing attributes.');
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to remove attributes.');
      return;
    }
    setError(null);
    clearAttributeAutosaveTimer(attr.id);
    lastSavedAttributeFieldsRef.current.delete(attr.id);
    try {
      await api(
        `/api/blueprints/${createdBlueprint.id}/recipe-card-tabs/${tabId}/attributes/${attr.id}/`,
        { method: 'DELETE' }
      );
      setAttributes((prev) => prev.filter((_, i) => i !== idx));
      void loadMethodologyFlags(createdBlueprint.id, tabId);
    } catch (e: any) {
      setError(e.message || 'Failed to delete attribute');
    }
  };

  const handleCreateAttribute = () => {
    if (!canSaveBlueprint) {
      setError('You do not have permission to add attributes.');
      return;
    }
    if (!newAttributeName.trim() || !newAttributeDescription.trim() || !createdBlueprint?.id) return;
    if (!activeRecipeTabId) {
      setError('Select a recipe card tab before adding attributes.');
      return;
    }
    setError(null);
    api<RecipeAttributeSet>(`/api/blueprints/${createdBlueprint.id}/recipe-attributes/`, {
      method: 'POST',
      body: JSON.stringify({
        attribute_name: newAttributeName.trim(),
        description_text: newAttributeDescription.trim(),
        details_text: '',
        constraints_text: '',
        display_order: attributes.length + 100,
        tab_id: activeRecipeTabId,
      }),
    })
      .then(async (created) => {
        invalidateCombinationOptionsCache();
        lastSavedAttributeFieldsRef.current.set(created.id, {
          details_text: created.details_text || '',
          constraints_text: created.constraints_text || '',
          has_been_evidenced: false,
        });
        if (activeRecipeTabId && createdBlueprint?.id) {
          await loadTabAttributes(createdBlueprint.id, activeRecipeTabId);
        } else {
          setAttributes((prev) => [...prev, { ...created, has_been_evidenced: false }]);
        }
        setShowAddAttributeModal(false);
        setNewAttributeName('');
        setNewAttributeDescription('');
      })
      .catch((e: any) => {
        setError(e.message || 'Failed to add attribute');
      });
  };

  const addManualOption = async (attributeSetId: number) => {
    if (!createdBlueprint?.id) return;
    const optionText = (optionDrafts[attributeSetId] || '').trim();
    if (!optionText) return;
    setError(null);
    try {
      const currentCount = attributeOptions.filter((o) => o.attribute_set === attributeSetId).length;
      const created = await api<RecipeAttributeOption>(`/api/blueprints/${createdBlueprint.id}/recipe-attribute-options/`, {
        method: 'POST',
        body: JSON.stringify({
          attribute_set: attributeSetId,
          option_text: optionText,
          generation_source: 'user',
          is_user_added: true,
          display_order: currentCount,
        }),
      });
      invalidateCombinationOptionsCache();
      setAttributeOptions((prev) => [...prev, created]);
      setOptionDrafts((prev) => ({ ...prev, [attributeSetId]: '' }));
    } catch (e: any) {
      setError(e.message || 'Failed to add option');
    }
  };

  const openExploratoryModal = (attributeSetId: number) => {
    setExploratoryAttributeId(attributeSetId);
    setExploratoryText('');
    setExploratoryEvidenced(false);
    setShowExploratoryModal(true);
  };

  const submitExploratoryInput = async () => {
    if (!createdBlueprint?.id || !exploratoryAttributeId || !exploratoryText.trim()) return;
    if (!activeCombinationId) {
      setError('Create or select a combination before adding exploratory input.');
      return;
    }
    if (!canSaveBlueprint) {
      setError('You do not have permission to add options to this blueprint.');
      return;
    }
    setError(null);
    try {
      const currentCount = attributeOptions.filter((o) => o.attribute_set === exploratoryAttributeId).length;
      const created = await api<RecipeAttributeOption>(`/api/blueprints/${createdBlueprint.id}/recipe-attribute-options/`, {
        method: 'POST',
        body: JSON.stringify({
          attribute_set: exploratoryAttributeId,
          option_text: exploratoryText.trim(),
          option_notes: exploratoryEvidenced ? 'evidenced=true' : 'evidenced=false',
          generation_source: 'user',
          is_user_added: true,
          display_order: currentCount,
          combination: activeCombinationId,
        }),
      });
      setAttributeOptions((prev) => {
        const next = [...prev, created];
        if (activeCombinationId != null) {
          combinationOptionsCacheRef.current.set(activeCombinationId, next);
        }
        return next;
      });
      setShowExploratoryModal(false);
      setExploratoryAttributeId(null);
      setExploratoryText('');
      setExploratoryEvidenced(false);
      void loadMethodologyFlags(createdBlueprint.id, activeRecipeTabIdRef.current);
    } catch (e: any) {
      setError(e.message || 'Failed to add exploratory input');
    }
  };

  const loadAttributeOutputHistory = async (attributeId: number) => {
    if (!createdBlueprint?.id) return [];
    const data = await api<RecipeAttributeOutputVersion[]>(
      `/api/blueprints/${createdBlueprint.id}/recipe-attributes/${attributeId}/output-history/`
    );
    const next = Array.isArray(data) ? data : [];
    setAttributeOutputHistory((prev) => ({ ...prev, [attributeId]: next }));

    // Once history is available, pin this combination to the version that matches
    // its saved selections (if it does not already have an explicit choice).
    const comboId = activeCombinationId;
    if (comboId != null) {
      setActiveOutputVersionByCombination((prev) => {
        if (prev[comboId]?.[attributeId] != null) return prev;
        const selectedIds = selectedOptionByAttribute[attributeId] || [];
        if (selectedIds.length === 0) return prev;
        const inferred = inferOutputVersionsFromSelections(
          { [attributeId]: selectedIds },
          { [attributeId]: next }
        );
        if (inferred[attributeId] == null) return prev;
        return setActiveOutputVersionForCombination(
          prev,
          comboId,
          attributeId,
          inferred[attributeId]
        );
      });
    }

    return next;
  };

  const openRegenerateAttributeModal = (attr: RecipeAttributeSet) => {
    setRegenerateAttributeId(attr.id);
    setRegenerateDetailsText(attr.details_text || '');
    setRegenerateConstraintsText(attr.constraints_text || '');
    setShowRegenerateAttributeModal(true);
  };

  const submitRegenerateAttribute = async () => {
    if (!createdBlueprint?.id || !regenerateAttributeId) return;
    if (!canSaveBlueprint) {
      setError('You do not have permission to regenerate attribute options.');
      return;
    }
    setError(null);
    setIsRegeneratingAttribute(true);
    try {
      const response = await api<{
        history?: RecipeAttributeOutputVersion[];
      }>(`/api/blueprints/${createdBlueprint.id}/recipe-attributes/${regenerateAttributeId}/regenerate-ai/`, {
        method: 'POST',
        body: JSON.stringify({
          details_text: regenerateDetailsText,
          constraints_text: regenerateConstraintsText,
        }),
      });

      invalidateCombinationOptionsCache();
      const refreshed = await loadAllOptions(createdBlueprint.id, activeCombinationId, {
        showLoading: false,
        writeCache: true,
      });
      if (activeRecipeTabId && createdBlueprint?.id) {
        await loadTabAttributes(createdBlueprint.id, activeRecipeTabId);
      } else {
        await loadRecipeAttributes(createdBlueprint.id);
      }
      setSelectedOptionByAttribute((prev) => {
        const next = { ...prev };
        const selectedIds = next[regenerateAttributeId] || [];
        const filtered = selectedIds.filter((optionId) =>
          refreshed.some((opt) => opt.id === optionId)
        );
        if (filtered.length > 0) {
          next[regenerateAttributeId] = filtered;
        } else {
          delete next[regenerateAttributeId];
        }
        return next;
      });
      if (Array.isArray(response?.history)) {
        const history = response.history || [];
        setAttributeOutputHistory((prev) => ({ ...prev, [regenerateAttributeId]: history }));
        const newCurrent = history.find((version) => version.is_current) ?? null;
        const previousCurrent =
          history
            .filter((version) => !version.is_current)
            .sort((a, b) => b.version_number - a.version_number)[0] ?? null;

        // Scope output versions per combination:
        // - active combination adopts the new current output
        // - other combinations that were implicitly on "current" stay on the
        //   previous output so their locked selections are not disrupted
        setActiveOutputVersionByCombination((prev) => {
          let next = { ...prev };
          for (const combo of savedCombinations) {
            if (combo.id === activeCombinationId) continue;
            const existing = next[combo.id]?.[regenerateAttributeId];
            if (existing == null && previousCurrent) {
              next = setActiveOutputVersionForCombination(
                next,
                combo.id,
                regenerateAttributeId,
                previousCurrent.id
              );
            }
          }
          if (activeCombinationId != null) {
            next = setActiveOutputVersionForCombination(
              next,
              activeCombinationId,
              regenerateAttributeId,
              newCurrent?.id ?? null
            );
          }
          return next;
        });
      }
      setShowRegenerateAttributeModal(false);
      setRegenerateAttributeId(null);
      showTransientSaveNotice('Attribute options regenerated', 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate attribute options');
    } finally {
      setIsRegeneratingAttribute(false);
    }
  };

  const deleteOption = async (optionId: number) => {
    if (!createdBlueprint?.id) return;
    if (!canSaveBlueprint) {
      setError('You do not have permission to remove options.');
      return;
    }
    setError(null);
    try {
      const removed = attributeOptions.find((o) => o.id === optionId);
      const isExploratory = Boolean((removed?.option_notes || '').includes('evidenced='));
      await api(`/api/blueprints/${createdBlueprint.id}/recipe-attribute-options/${optionId}/`, {
        method: 'DELETE',
      });
      setAttributeOptions((prev) => {
        const next = prev.filter((o) => o.id !== optionId);
        if (isExploratory && activeCombinationId != null) {
          combinationOptionsCacheRef.current.set(activeCombinationId, next);
        } else {
          invalidateCombinationOptionsCache();
        }
        return next;
      });
      setSelectedOptionByAttribute((prev) => {
        const next: SelectedOptionsByAttribute = {};
        Object.entries(prev).forEach(([attributeId, selectedOptionIds]) => {
          const filtered = selectedOptionIds.filter((id) => id !== optionId);
          if (filtered.length > 0) {
            next[Number(attributeId)] = filtered;
          }
        });
        return next;
      });
      if (isExploratory) {
        void loadMethodologyFlags(createdBlueprint.id, activeRecipeTabIdRef.current);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to remove option');
    }
  };

  const selectOption = (attributeSetId: number, optionId: number) => {
    setSelectedOptionByAttribute((prev) => {
      if (isRajaProject) {
        return toggleOptionSelection(prev, attributeSetId, optionId);
      }
      return { ...prev, [attributeSetId]: [optionId] };
    });
  };

  const requestDeleteAttribute = (attributeId: number) => {
    setPendingRemoveAttributeId(attributeId);
    setShowRemoveAttributeModal(true);
  };

  const confirmDeleteAttribute = async () => {
    if (pendingRemoveAttributeId == null) return;
    const idx = attributes.findIndex((a) => a.id === pendingRemoveAttributeId);
    if (idx < 0) {
      setShowRemoveAttributeModal(false);
      setPendingRemoveAttributeId(null);
      return;
    }
    await deleteAttributeSet(idx);
    setSelectedOptionByAttribute((prev) => {
      const next = { ...prev };
      delete next[pendingRemoveAttributeId];
      return next;
    });
    setShowRemoveAttributeModal(false);
    setPendingRemoveAttributeId(null);
  };

  const buildSelectionPayload = (): RecipeCombinationSelectionPayload[] | null => {
    const selectionPayload: RecipeCombinationSelectionPayload[] = [];
    Object.entries(selectedOptionByAttribute).forEach(([attributeSetId, optionIds]) => {
      if (optionIds.length === 0) return;
      if (isRajaProject && optionIds.length > 1) {
        const texts = optionIds
          .map((id) => attributeOptions.find((o) => o.id === id)?.option_text?.trim())
          .filter((text): text is string => Boolean(text));
        selectionPayload.push({
          attribute_set: Number(attributeSetId),
          option: optionIds[0],
          snapshot_option_text: texts.join(RAJA_MULTI_OPTION_DELIMITER),
        });
      } else {
        selectionPayload.push({
          attribute_set: Number(attributeSetId),
          option: optionIds[0],
        });
      }
    });
    if (selectionPayload.length === 0) {
      setError('Add at least one option before saving a draft combination.');
      return null;
    }
    return selectionPayload;
  };

  const persistCurrentCombinationSelections = async (): Promise<boolean> => {
    if (!createdBlueprint?.id || !activeCombinationId) return false;
    if (!canSaveBlueprint) {
      setError('You do not have permission to save recipe combinations.');
      return false;
    }

    const selectionPayload = buildSelectionPayload();
    if (!selectionPayload) return false;

    const activeDraft = savedCombinations.find((c) => c.id === activeCombinationId);
    if (!activeDraft) return false;

    setError(null);
    setIsSavingDraftRecipe(true);
    try {
      const updated = await api<RecipeCombination>(
        `/api/blueprints/${createdBlueprint.id}/recipe-combinations/${activeDraft.id}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            combination_name: activeDraft.combination_name,
            selections: selectionPayload,
          }),
        }
      );
      setSavedCombinations((prev) =>
        sortCombinationsOldestFirst(prev.map((c) => (c.id === updated.id ? updated : c)))
      );
      return true;
    } catch (e: any) {
      setError(e.message || 'Failed to save combination');
      return false;
    } finally {
      setIsSavingDraftRecipe(false);
    }
  };

  const saveActiveCombination = async () => {
    if (!createdBlueprint?.id || !activeCombinationId) return;
    if (!canSaveBlueprint) {
      setError('You do not have permission to save recipe combinations.');
      return;
    }

    const trimmedName = draftName.trim();
    if (!trimmedName) {
      setError('Please provide a name for this recipe combination.');
      return;
    }

    const selectionPayload = buildSelectionPayload();
    if (!selectionPayload) return;

    const activeDraft = savedCombinations.find((c) => c.id === activeCombinationId);
    if (!activeDraft) return;

    setError(null);
    setIsSavingDraftRecipe(true);
    try {
      const updated = await api<RecipeCombination>(
        `/api/blueprints/${createdBlueprint.id}/recipe-combinations/${activeDraft.id}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            combination_name: trimmedName,
            selections: selectionPayload,
          }),
        }
      );
      setSavedCombinations((prev) =>
        sortCombinationsOldestFirst(prev.map((c) => (c.id === updated.id ? updated : c)))
      );
      setShowSaveDraftModal(false);
      setDraftName('');
      showTransientSaveNotice(`Saved "${updated.combination_name}"`, 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save combination');
    } finally {
      setIsSavingDraftRecipe(false);
    }
  };

  const createNewCombination = async (combinationName: string) => {
    if (!createdBlueprint?.id) return;
    if (!canSaveBlueprint) {
      setError('You do not have permission to save recipe combinations.');
      return;
    }

    setError(null);
    setIsSavingDraftRecipe(true);
    setRecipePageView('recipe-card');
    try {
      const created = await api<RecipeCombination>(`/api/blueprints/${createdBlueprint.id}/recipe-combinations/`, {
        method: 'POST',
        body: JSON.stringify({
          combination_name: combinationName,
          generation_mode: generationMode ?? 'manual',
          tab_id: activeRecipeTabIdRef.current,
          selections: [],
        }),
      });
      const nextCombinations = sortCombinationsOldestFirst([...savedCombinations, created]);
      setSavedCombinations(nextCombinations);
      setActiveCombinationId(created.id);
      await loadCombinationWorkspace(createdBlueprint.id, created.id, {
        reloadAttributes: false,
        showOptionsLoading: false,
      });
      const freshOptions = combinationOptionsCacheRef.current.get(created.id) ?? [];
      setSelectedOptionByAttribute(
        autoSelectSingleOptions(
          {},
          freshOptions,
          attributesRef.current.map((attr) => attr.id),
        )
      );
      showTransientSaveNotice(`Created "${created.combination_name}"`, 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to create combination');
    } finally {
      setIsSavingDraftRecipe(false);
    }
  };

  const handleSaveDraftClick = () => {
    if (!activeCombinationId) {
      setError('Create a combination first using New combination.');
      return;
    }
    const selectionPayload = buildSelectionPayload();
    if (!selectionPayload) return;

    const activeDraft = savedCombinations.find((c) => c.id === activeCombinationId);
    setDraftName(activeDraft?.combination_name ?? '');
    setShowSaveDraftModal(true);
  };

  const handleClearSelection = () => {
    setSelectedOptionByAttribute({});
    setError(null);
  };

  const handleCombinationChange = (combinationId: number) => {
    if (combinationId === activeCombinationId || !createdBlueprint?.id) return;

    const combo = savedCombinations.find((c) => c.id === combinationId);
    let nextSelections: SelectedOptionsByAttribute = {};
    if (combo) {
      const optionsForParse =
        combinationOptionsCacheRef.current.get(combinationId) ?? attributeOptions;
      nextSelections = autoSelectSingleOptions(
        combinationSelectionsFrom(combo, optionsForParse, isRajaProject),
        optionsForParse,
        attributes.map((attr) => attr.id),
      );
      setSelectedOptionByAttribute(nextSelections);
    } else {
      setSelectedOptionByAttribute({});
    }
    setActiveCombinationId(combinationId);

    // If this combination has no remembered output versions yet, infer them from
    // its saved selections so previously locked combos stay on their output.
    setActiveOutputVersionByCombination((prev) => {
      if (prev[combinationId] && Object.keys(prev[combinationId]).length > 0) {
        return prev;
      }
      const inferred = inferOutputVersionsFromSelections(nextSelections, attributeOutputHistory);
      if (Object.keys(inferred).length === 0) return prev;
      return { ...prev, [combinationId]: inferred };
    });

    const cached = combinationOptionsCacheRef.current.get(combinationId);
    if (cached) {
      setAttributeOptions(cached);
      return;
    }

    void loadAllOptions(createdBlueprint.id, combinationId, { showLoading: false });
  };

  const handleNewCombination = () => {
    if (!createdBlueprint?.id) return;
    setError(null);
    void createNewCombination(getNextCombinationName(savedCombinations));
  };

  const startRenameCombination = (combination: RecipeCombination) => {
    setOpenCombinationMenuId(null);
    setRenamingCombinationId(combination.id);
    setRenameCombinationValue(combination.combination_name);
  };

  const commitRenameCombination = async () => {
    if (!createdBlueprint?.id || renamingCombinationId == null) return;
    const trimmed = renameCombinationValue.trim();
    const combo = savedCombinations.find((c) => c.id === renamingCombinationId);
    if (!combo || !trimmed || trimmed === combo.combination_name) {
      setRenamingCombinationId(null);
      setRenameCombinationValue('');
      return;
    }
    setError(null);
    try {
      const updated = await api<RecipeCombination>(
        `/api/blueprints/${createdBlueprint.id}/recipe-combinations/${combo.id}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({ combination_name: trimmed }),
        }
      );
      setSavedCombinations((prev) =>
        sortCombinationsOldestFirst(prev.map((c) => (c.id === updated.id ? updated : c)))
      );
    } catch (e: any) {
      setError(e.message || 'Failed to rename combination');
    } finally {
      setRenamingCombinationId(null);
      setRenameCombinationValue('');
    }
  };

  const cancelRenameCombination = () => {
    setRenamingCombinationId(null);
    setRenameCombinationValue('');
  };

  const requestDeleteCombination = (combinationId: number) => {
    setOpenCombinationMenuId(null);
    setPendingDeleteCombinationId(combinationId);
    setShowDeleteCombinationModal(true);
  };

  const confirmDeleteCombination = async () => {
    if (!createdBlueprint?.id || pendingDeleteCombinationId == null) return;
    setError(null);
    setIsSavingDraftRecipe(true);
    try {
      await api(
        `/api/blueprints/${createdBlueprint.id}/recipe-combinations/${pendingDeleteCombinationId}/`,
        { method: 'DELETE' }
      );
      const remaining = savedCombinations.filter((c) => c.id !== pendingDeleteCombinationId);
      setSavedCombinations(remaining);
      if (activeCombinationId === pendingDeleteCombinationId) {
        const next = remaining[remaining.length - 1] ?? null;
        setActiveCombinationId(next?.id ?? null);
        if (next) {
          const optionsForParse =
            combinationOptionsCacheRef.current.get(next.id) ?? attributeOptions;
          setSelectedOptionByAttribute(
            autoSelectSingleOptions(
              combinationSelectionsFrom(next, optionsForParse, isRajaProject),
              optionsForParse,
              attributesRef.current.map((attr) => attr.id),
            )
          );
          void loadAllOptions(createdBlueprint.id, next.id, { showLoading: false });
        } else {
          setSelectedOptionByAttribute({});
        }
      }
      setShowDeleteCombinationModal(false);
      setPendingDeleteCombinationId(null);
    } catch (e: any) {
      setError(e.message || 'Failed to delete combination');
    } finally {
      setIsSavingDraftRecipe(false);
    }
  };

  const activeCombination = useMemo(
    () => savedCombinations.find((c) => c.id === activeCombinationId) ?? null,
    [savedCombinations, activeCombinationId]
  );

  const selectedOptionsPayload = useMemo(() => {
    return Object.entries(selectedOptionByAttribute).flatMap(([attributeSetId, optionIds]) =>
      optionIds.map((optionId) => ({
        attribute_set: Number(attributeSetId),
        option: optionId,
      }))
    );
  }, [selectedOptionByAttribute]);

  const innovationPlatformText = useMemo(() => {
    return [job.trim(), tension.trim()].filter(Boolean).join(' ');
  }, [job, tension]);

  const selectedAttributeCount = useMemo(() => {
    return attributes.filter((attr) => (selectedOptionByAttribute[attr.id] || []).length > 0).length;
  }, [attributes, selectedOptionByAttribute]);

  const allAttributesSelected =
    attributes.length > 0 && selectedAttributeCount === attributes.length;

  const canToggleProductIdea = activeCombinationId != null;

  const visibleMethodologyFlags = showAllMethodologyNotes
    ? methodologyFlags
    : methodologyFlags.slice(0, 3);

  return (
    <div className="w-full h-full bg-gray-50 flex flex-col relative">
      <div className={`border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between ${step === 2 || workspaceMode ? 'hidden' : ''}`}>
        <div className="flex items-center gap-3">
          <button
            onClick={handleHeaderBack}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title={step > 1 ? 'Back to previous step' : 'Back to blueprints'}
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">New Blueprint Workflow</h1>
            <p className="text-xs text-gray-500">Step {step} of {TOTAL_STEPS}</p>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-auto ${step === 2 || step === 3 ? 'p-0' : 'p-6'}`}>
        {error && (
          <div
            className={`relative z-10 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 ${
              step === 3 ? 'mb-10' : 'mb-4'
            }`}
          >
            {error}
          </div>
        )}
        {createdBlueprint?.id && !canSaveBlueprint && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            You have view-only access to this blueprint: you can browse jobs, tensions, recipe cards,
            tabs, combinations, and concepts, but editing and AI generation are disabled.
          </div>
        )}

        {showAddAttributeModal && (
          <div
            className="fixed inset-0 z-[12000] bg-black/35 flex items-center justify-center p-6"
            role="dialog"
            aria-modal
            aria-labelledby="add-new-attribute-title"
          >
            <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
              <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
                <div className="min-w-0 pr-2">
                  <h2 id="add-new-attribute-title" className="text-xl font-bold text-gray-900">
                    Add New Attribute
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    Create a custom attribute for your product.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddAttributeModal(false);
                    setNewAttributeName('');
                    setNewAttributeDescription('');
                  }}
                  className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Close add attribute modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 pb-6 space-y-5">
                <div className="flex items-start gap-2 text-sm text-gray-500 leading-relaxed">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" aria-hidden />
                  <p>
                    Adding a new attribute can change/update other attributes. Note: You can always
                    remove new attributes later if they affect the quality of your outputs.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="new-attribute-name"
                    className="text-sm font-semibold text-gray-500 block mb-2"
                  >
                    Attribute Name *
                  </label>
                  <input
                    id="new-attribute-name"
                    value={newAttributeName}
                    onChange={(e) => setNewAttributeName(e.target.value)}
                    placeholder="e.g. Sustainability, Texture, Functional Benefit"
                    className="w-full rounded-lg border border-[#E0DEE8] bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#43C8F5] focus:ring-2 focus:ring-[#43C8F5]/20 outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="new-attribute-description"
                    className="text-sm font-semibold text-gray-500 block mb-2"
                  >
                    Add Description *
                  </label>
                  <textarea
                    id="new-attribute-description"
                    value={newAttributeDescription}
                    onChange={(e) => setNewAttributeDescription(e.target.value)}
                    placeholder="e.g. Defines environmental impact and sustainability credentials of the product"
                    className="w-full rounded-lg border border-[#E0DEE8] bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 h-28 resize-y focus:border-[#43C8F5] focus:ring-2 focus:ring-[#43C8F5]/20 outline-none"
                  />
                </div>

                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={handleCreateAttribute}
                    disabled={!newAttributeName.trim() || !newAttributeDescription.trim() || !canSaveBlueprint}
                    className="inline-flex items-center justify-center rounded-full border border-[#43C8F5] bg-white px-10 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showExploratoryModal && (
          <div
            className="fixed inset-0 z-[12000] bg-black/35 flex items-center justify-center p-6"
            role="dialog"
            aria-modal
            aria-labelledby="add-exploratory-input-title"
          >
            <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
              <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
                <div className="min-w-0 pr-2">
                  <h2 id="add-exploratory-input-title" className="text-xl font-bold text-gray-900">
                    Add Exploratory Input
                  </h2>
                  <p className="mt-2 text-sm text-gray-500">
                    Contribute an exploratory to this attribute.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowExploratoryModal(false);
                    setExploratoryAttributeId(null);
                    setExploratoryText('');
                    setExploratoryEvidenced(false);
                  }}
                  className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Close exploratory input modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 pb-6 space-y-5">
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-700">Attribute:</span>{' '}
                  {attributes.find((a) => a.id === exploratoryAttributeId)?.attribute_name || '—'}
                </p>

                <div>
                  <label
                    htmlFor="exploratory-input-text"
                    className="text-sm font-semibold text-gray-500 block mb-2"
                  >
                    Add Input *
                  </label>
                  <textarea
                    id="exploratory-input-text"
                    value={exploratoryText}
                    onChange={(e) => setExploratoryText(e.target.value)}
                    placeholder="Add your additional input here..."
                    className="w-full rounded-lg border border-[#E0DEE8] bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 h-32 resize-y focus:border-[#43C8F5] focus:ring-2 focus:ring-[#43C8F5]/20 outline-none"
                  />
                </div>

                <label className="flex items-center gap-2.5 text-sm text-gray-500">
                  <input
                    type="checkbox"
                    checked={exploratoryEvidenced}
                    onChange={(e) => setExploratoryEvidenced(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#655FA8] focus:ring-[#655FA8]/30"
                  />
                  <span>Has this been evidenced?</span>
                </label>

                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={submitExploratoryInput}
                    disabled={!exploratoryText.trim() || !canSaveBlueprint}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#43C8F5] bg-white px-6 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" aria-hidden />
                    Add Input
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRegenerateAttributeModal && (
          <div
            className="fixed inset-0 z-[12000] bg-black/35 flex items-center justify-center p-6"
            role="dialog"
            aria-modal
            aria-labelledby="regenerate-attribute-title"
          >
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
              <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
                <div className="min-w-0 pr-2">
                  <h2 id="regenerate-attribute-title" className="text-xl font-bold text-gray-900">
                    {attributes.find((a) => a.id === regenerateAttributeId)?.attribute_name || 'Attribute'}
                  </h2>
                  <div className="mt-3 flex items-start gap-2 text-sm text-gray-500 leading-relaxed">
                    <Info className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" aria-hidden />
                    <p>
                      Editing an attribute can change/update other attributes. Note: You can
                      always edit it later if they affect the quality of your outputs.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRegenerateAttributeModal(false)}
                  className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Close regenerate attribute modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 pb-6 space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Details *</label>
                  <textarea
                    value={regenerateDetailsText}
                    onChange={(e) => setRegenerateDetailsText(e.target.value)}
                    className="w-full rounded-lg border border-[#E0DEE8] bg-white px-4 py-2.5 text-sm text-gray-900 h-20 resize-y focus:border-[#43C8F5] focus:ring-2 focus:ring-[#43C8F5]/20 outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Constraints *</label>
                  <textarea
                    value={regenerateConstraintsText}
                    onChange={(e) => setRegenerateConstraintsText(e.target.value)}
                    className="w-full rounded-lg border border-[#E0DEE8] bg-white px-4 py-2.5 text-sm text-gray-900 h-20 resize-y focus:border-[#43C8F5] focus:ring-2 focus:ring-[#43C8F5]/20 outline-none"
                  />
                </div>
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => setShowRegenerateAttributeModal(false)}
                    className="inline-flex items-center justify-center rounded-full border border-[#D1D1D1] bg-white px-6 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitRegenerateAttribute()}
                    disabled={isRegeneratingAttribute || !canSaveBlueprint}
                    className="inline-flex items-center justify-center rounded-full bg-[#655FA8] px-8 py-2 text-sm font-semibold text-white hover:bg-[#544fa0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRegeneratingAttribute ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteTabModal && pendingDeleteTabId != null ? (
          <div className="fixed inset-0 z-[12100] bg-black/35 backdrop-blur-sm flex items-center justify-center p-6">
            <div
              className="w-full max-w-md rounded-2xl border border-[#D1D1D1] bg-white shadow-2xl overflow-hidden"
              role="dialog"
              aria-modal
              aria-labelledby="delete-tab-title"
            >
              <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
                <h2 id="delete-tab-title" className="text-lg font-bold text-gray-900">
                  Delete Tab
                </h2>
                <button
                  type="button"
                  onClick={closeDeleteTabModal}
                  className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Close"
                  disabled={recipeTabsBusy}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 pb-6">
                <p className="text-sm text-gray-500 leading-relaxed">
                  Deleting a tab will remove its recipe card details for this brand and format.
                  Confirm this action below.
                </p>
              </div>
              <div className="px-6 pb-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => void confirmDeleteRecipeTab()}
                  disabled={!canSaveBlueprint || recipeTabsBusy}
                  className="rounded-full border border-[#3191C8] bg-transparent px-6 py-2.5 text-sm font-semibold text-[#3191C8] hover:bg-[#3191C8]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {recipeTabsBusy ? 'Deleting…' : 'Yes, delete'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showRemoveAttributeModal && (
          <div
            className="fixed inset-0 z-[12100] bg-black/35 flex items-center justify-center p-6"
            role="dialog"
            aria-modal
            aria-labelledby="delete-attribute-title"
          >
            <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
              <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
                <div className="min-w-0 pr-2">
                  <h2 id="delete-attribute-title" className="text-xl font-bold text-gray-900">
                    Delete Attribute
                  </h2>
                  <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                    Deleting an attribute can change/update other attributes. Confirm this action
                    below.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowRemoveAttributeModal(false);
                    setPendingRemoveAttributeId(null);
                  }}
                  className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Close delete attribute modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 pb-6 flex justify-end">
                <button
                  type="button"
                  onClick={confirmDeleteAttribute}
                  disabled={!canSaveBlueprint}
                  className="inline-flex items-center justify-center rounded-full border border-[#43C8F5] bg-white px-8 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Yes, delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showSaveDraftModal && (
          <div className="fixed inset-0 z-[12200] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
              <div className="px-5 py-4 flex items-center justify-between border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Save draft</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveDraftModal(false);
                    setDraftName('');
                  }}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Close save draft modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-5 py-4">
                <label className="text-sm font-medium text-gray-900 block mb-2">Name this recipe combination</label>
                <input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="e.g. Bold umami variant"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                />
              </div>
              <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveDraftModal(false);
                    setDraftName('');
                  }}
                  className="rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium px-4 py-2 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void saveActiveCombination()}
                  disabled={isSavingDraftRecipe || !draftName.trim() || !canSaveBlueprint}
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2 disabled:opacity-50"
                >
                  {isSavingDraftRecipe ? 'Saving...' : 'Save draft'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="max-w-4xl mx-auto w-full space-y-5">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Basics & Innovation platform</h2>
              <p className="text-sm text-gray-500 mt-1">
                Start the same way as the classic flow: define blueprint basics, then the innovation platform.
              </p>
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Blueprint name (optional)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm bg-white shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description (optional)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm h-24 bg-white shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
            <select
              value={market}
              onChange={(e) => void handleMarketChange(e.target.value)}
              disabled={marketLocked || isMarketAutosaving}
              className={`w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none ${
                marketLocked || isMarketAutosaving
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'bg-white'
              }`}
            >
              <option value="">Select market...</option>
              {projectMarkets.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
              <option value="All of the above">All of the above</option>
            </select>
            {isMarketAutosaving && (
              <p className="text-xs text-indigo-600 flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
                Saving blueprint…
              </p>
            )}
            {marketLocked && market && !isMarketAutosaving && (
              <p className="text-xs text-gray-500 italic">
                Market is locked after selection. Blueprint saved — you can close and resume later.
              </p>
            )}

            <section className="rounded-xl border-2 border-platform bg-white overflow-hidden transition-shadow duration-300 hover:shadow-[0_0_15px_rgba(195,215,79,0.4)]">
              <div className="px-4 py-3 border-b border-platform bg-platform-light">
                <h3 className="text-sm font-semibold text-platform-dark">Innovation platform</h3>
                <p className="text-xs text-gray-500 mt-0.5">Define your strategic positioning</p>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-xs font-medium text-gray-600">Job to be Done *</label>
                    <div className="flex items-center gap-2">
                      {jobLocked ? (
                        <span
                          className="shrink-0 inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600"
                          aria-label="Job to be done locked"
                        >
                          Locked
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void handleLockJobToBeDone()}
                          disabled={
                            innovationPlatformLocked ||
                            !market.trim() ||
                            isMarketAutosaving ||
                            jobLockBusy ||
                            !job.trim()
                          }
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {jobLockBusy ? (
                            <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
                          ) : (
                            <span>Lock</span>
                          )}
                        </button>
                      )}
                      {jtbdStatements.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setShowJtbdPickerModal(true)}
                          disabled={
                            innovationPlatformLocked || !market.trim() || isMarketAutosaving || jobLocked
                          }
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <List className="w-3 h-3" aria-hidden />
                          Jobs to be done
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAssistClick('job')}
                          disabled={
                            innovationPlatformLocked || !market.trim() || isMarketAutosaving || jobLocked
                          }
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Sparkles className="w-3 h-3" aria-hidden />
                          Assist
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm h-20 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none transition-all duration-200 ${
                      innovationPlatformLocked
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'hover:border-gray-400'
                    }`}
                    value={job}
                    onChange={(e) => setJob(e.target.value)}
                    disabled={innovationPlatformLocked || jobLocked}
                    placeholder="When xxxxxxx, I want to xxxxxxxxx, so I can xxxxxxxxxxxxx"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-xs font-medium text-gray-600">Tension *</label>
                    <div className="flex items-center gap-2">
                      {tensionLocked ? (
                        <span
                          className="shrink-0 inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600"
                          aria-label="Tension locked"
                        >
                          Locked
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void handleLockTension()}
                          disabled={
                            innovationPlatformLocked ||
                            !market.trim() ||
                            isMarketAutosaving ||
                            tensionLockBusy ||
                            !tension.trim() ||
                            !jobLocked
                          }
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {tensionLockBusy ? (
                            <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
                          ) : (
                            <span>Lock</span>
                          )}
                        </button>
                      )}
                      {tensionStatements.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setShowTensionPickerModal(true)}
                          disabled={
                            innovationPlatformLocked ||
                            !market.trim() ||
                            isMarketAutosaving ||
                            tensionLocked
                          }
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <List className="w-3 h-3" aria-hidden />
                          Tensions
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAssistClick('tension')}
                          disabled={
                            innovationPlatformLocked ||
                            !market.trim() ||
                            isMarketAutosaving ||
                            !jobLocked
                          }
                          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Sparkles className="w-3 h-3" aria-hidden />
                          Assist
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm h-20 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none transition-all duration-200 ${
                      innovationPlatformLocked
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'hover:border-gray-400'
                    }`}
                    value={tension}
                    onChange={(e) => setTension(e.target.value)}
                    disabled={innovationPlatformLocked || tensionLocked}
                    placeholder="But xxxxxxxxxxxxxxx"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label className="text-xs font-medium text-gray-600">Benefit *</label>
                    {benefitStatements.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setShowBenefitPickerModal(true)}
                        disabled={
                          innovationPlatformLocked || !market.trim() || isMarketAutosaving
                        }
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <List className="w-3 h-3" aria-hidden />
                        Benefits
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAssistClick('benefit')}
                        disabled={
                          innovationPlatformLocked ||
                          !market.trim() ||
                          isMarketAutosaving ||
                          !jobLocked ||
                          !tensionLocked
                        }
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sparkles className="w-3 h-3" aria-hidden />
                        Assist
                      </button>
                    )}
                  </div>
                  <textarea
                    className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm h-20 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none transition-all duration-200 ${
                      innovationPlatformLocked
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'hover:border-gray-400'
                    }`}
                    value={benefit}
                    onChange={(e) => setBenefit(e.target.value)}
                    disabled={innovationPlatformLocked}
                    placeholder="What key benefit does your product deliver?"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">Brand Choice *</label>
                  <textarea
                    className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm h-20 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none transition-all duration-200 ${
                      innovationPlatformLocked
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'hover:border-gray-400'
                    }`}
                    value={permission}
                    onChange={(e) => setPermission(e.target.value)}
                    disabled={innovationPlatformLocked}
                    placeholder="What gives customers permission to believe in your solution?"
                  />
                </div>
              </div>
            </section>
            {innovationPlatformLocked && (
              <p className="text-xs text-gray-500 italic">
                Innovation platform is locked after you configure the recipe card.
              </p>
            )}
            {!canGoNext && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Select a market and fill all Innovation platform fields to continue.
              </p>
            )}
            {canGoNext && loading && (
              <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                Checking your permissions…
              </p>
            )}
            {canGoNext && !loading && capsError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                Could not load your permissions ({capsError}). Refresh the page or sign in again.
              </p>
            )}
            {canGoNext && !loading && !capsError && !canAdvanceFromStep1 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {!createdBlueprint?.id ? (
                  <>
                    You need both <strong>Create blueprint</strong> and <strong>Save blueprint</strong> on your
                    organization role to continue. Ask an admin to update your role, or use an account with those
                    permissions.
                  </>
                ) : (
                  <>
                    You need <strong>Save blueprint</strong> permission to update this blueprint. Ask an org admin
                    to update your role.
                  </>
                )}
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="-mx-6 -my-6 h-full bg-white flex flex-col">
            {/* Page header */}
            <div className="px-8 pt-6 pb-4 border-b border-[#EEEEEE]">
              <InnovationWorkspaceButton onClick={handleHeaderBack} className="mb-4" />

              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="min-w-0 flex-1">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {projectDisplayName || createdBlueprint?.project_name || 'Project Name'}
                  </h1>

                  <div className="mt-5">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-sm font-bold text-gray-900">Innovation platform</h2>
                      {onViewInnovationPlatform ? (
                        <button
                          type="button"
                          onClick={onViewInnovationPlatform}
                          className="text-sm font-medium text-[#43C8F5] underline hover:text-[#2A9CC4] transition-colors"
                        >
                          View
                        </button>
                      ) : null}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
                      {innovationPlatformText ||
                        'Add a job to be done and tension in the Innovation Workspace to define your innovation platform.'}
                    </p>
                  </div>
                </div>

                <div className="w-full lg:w-80 shrink-0 rounded-xl border border-[#EEEEEE] bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Methodology notes</h3>
                  {methodologyFlags.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Info className="w-4 h-4 shrink-0" aria-hidden />
                      <span>No methodology notes added yet.</span>
                    </div>
                  ) : (
                    <>
                      <ul>
                        {visibleMethodologyFlags.map((flag, index) => (
                          <li
                            key={`${flag.type}-${flag.label}-${index}`}
                            className={index > 0 ? 'border-t border-[#EEEEEE]' : undefined}
                          >
                            <div className="flex items-center gap-3 py-3">
                              <MethodologyFlagIcon type={flag.type} />
                              <span className="flex-1 min-w-0 text-sm text-gray-800 leading-snug">
                                {flag.label}
                              </span>
                              <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 whitespace-nowrap">
                                {formatMethodologyTag(flag.evidenced)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {methodologyFlags.length > 3 ? (
                        <div className="flex justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => setShowAllMethodologyNotes((open) => !open)}
                            className="text-sm font-medium text-[#43C8F5] hover:text-[#2A9CC4] inline-flex items-center gap-1"
                          >
                            {showAllMethodologyNotes
                              ? 'View less'
                              : `View all (${methodologyFlags.length})`}
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                showAllMethodologyNotes ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="px-8 pt-4 pb-0">
              <RecipeCardTabControls
                tabs={recipeCardTabs}
                activeTabId={activeRecipeTabId}
                brands={recipeCardBrands}
                formats={recipeCardFormats}
                canSave={canSaveBlueprint}
                busy={recipeTabsBusy}
                onSelectTab={(tabId) => void handleRecipeTabSelect(tabId)}
                onCreateTab={() => void handleCreateRecipeTab()}
                onRenameTab={(tabId, tabName) => void handleRenameRecipeTab(tabId, tabName)}
                onDeleteTab={(tabId) => void handleDeleteRecipeTab(tabId)}
                onSelectBrand={(tabId, brandId) => void handleSelectRecipeTabBrand(tabId, brandId)}
                onSelectFormat={(tabId, formatId) => void handleSelectRecipeTabFormat(tabId, formatId)}
                onCreateBrand={handleCreateRecipeBrand}
                onCreateFormat={handleCreateRecipeFormat}
                onGenerateFormatIdeas={() => setShowFormatIdeasWorkInProgressModal(true)}
              />
            </div>

            <div className="flex-1 min-h-0 px-8 pb-4 flex flex-col">
              <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-[#D1D1D1] bg-[#F5F5F5] overflow-hidden">
              <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 bg-[#F5F5F5]">
                <button
                  type="button"
                  onClick={() => setShowAddAttributeModal(true)}
                  disabled={!canSaveBlueprint || !activeRecipeTabId}
                  className={recipeAddAttributePillClass}
                >
                  <Plus className="w-3.5 h-3.5" aria-hidden />
                  Add New Attribute
                </button>
                <button
                  type="button"
                  onClick={handleStep2PrimaryAction}
                  disabled={!canSaveBlueprint || isSaving || isRecipeAiGenerating}
                  className="inline-flex items-center gap-2 rounded-full bg-[#655FA8] text-white text-sm font-semibold px-6 py-2 hover:bg-[#544fa0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRecipeAiGenerating ? 'Generating...' : 'Recipe Card Builder'}
                </button>
              </div>

            {/* Attribute grid */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 bg-[#F5F5F5]">
              {isLoadingAttributes && (
                <div className="rounded-xl border border-[#D1D1D1] bg-white px-4 py-3 text-sm text-gray-500 mb-4">
                  Loading attributes...
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {attributes.map((attr, i) => (
                  <div
                    key={`${attr.attribute_name}-${attr.id ?? i}`}
                    className="rounded-xl border border-[#D1D1D1] bg-white overflow-hidden flex flex-col"
                  >
                    <div className="px-4 py-3 flex items-center justify-between gap-2 border-b border-[#D1D1D1] bg-white">
                      <h3 className="text-sm font-bold text-gray-900 truncate">
                        {attr.attribute_name}
                        {attr.is_user_added ? (
                          <span className="text-[10px] font-normal text-gray-500"> (User added)</span>
                        ) : null}
                      </h3>
                      <button
                        type="button"
                        onClick={() => requestDeleteAttribute(attr.id)}
                        disabled={!canSaveBlueprint}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-40 disabled:pointer-events-none"
                        aria-label={`Delete ${attr.attribute_name}`}
                        title="Delete attribute"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="p-4 space-y-4 flex-1 flex flex-col">
                      <div>
                        <div className="mb-2">
                          <span className="text-sm font-semibold text-gray-900">Details</span>
                        </div>
                        <textarea
                          value={attr.details_text || ''}
                          onChange={(e) => updateAttributeValue(i, e.target.value)}
                          onBlur={() => attr.id && flushAttributeAutosave(attr.id)}
                          className="w-full rounded-xl border border-[#D1D1D1] bg-white px-3 py-2.5 text-sm min-h-[88px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3191C8]/30 focus:border-[#3191C8] resize-y"
                          placeholder="Enter options separated by commas, e.g. apples, pears"
                        />
                        {parseDetailOptions(attr.details_text || '').length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {parseDetailOptions(attr.details_text || '').map((option, optionIdx) => (
                              <span
                                key={`${attr.id}-${optionIdx}-${option}`}
                                className="inline-flex items-center rounded-full bg-[#ddd7ef] px-2.5 py-0.5 text-[11px] font-medium text-[#2f3035]"
                              >
                                {option}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <label className="mt-3 flex items-center gap-2.5 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={Boolean(attr.has_been_evidenced)}
                            onChange={(e) => updateAttributeEvidenced(i, e.target.checked)}
                            disabled={!canSaveBlueprint}
                            className="w-4 h-4 rounded border-gray-300 text-[#655FA8] focus:ring-[#655FA8]/30"
                          />
                          <span>Has this been evidenced?</span>
                        </label>
                      </div>

                      <div>
                        <div className="mb-2">
                          <span className="text-sm font-semibold text-gray-900">Constraints</span>
                        </div>
                        <textarea
                          value={attr.constraints_text || ''}
                          onChange={(e) => updateAttributeConstraints(i, e.target.value)}
                          onBlur={() => attr.id && flushAttributeAutosave(attr.id)}
                          className="w-full rounded-xl border border-[#D1D1D1] bg-white px-3 py-2.5 text-sm min-h-[88px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3191C8]/30 focus:border-[#3191C8] resize-y"
                          placeholder="Add constraints associated with this attribute..."
                        />
                      </div>

                      {savingAttributeId === attr.id ? (
                        <p className="text-xs text-gray-500">Saving...</p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {!isLoadingAttributes && attributes.length === 0 && (
                <div className="text-center py-16 text-gray-500 text-sm">
                  No attributes yet. Use &ldquo;Add a new attribute&rdquo; to get started.
                </div>
              )}
            </div>
              </div>
            </div>
          </div>
        )}


        {step === 3 && (
          <div className="-mx-6 -my-6 h-full bg-white flex flex-col">
            <div className="px-8 pt-6 pb-2 border-b border-[#EEEEEE]">
              {workspaceMode ? (
                <InnovationWorkspaceButton onClick={handleHeaderBack} className="mb-4" />
              ) : null}

              <div className="flex flex-col lg:flex-row lg:items-start gap-6 pb-4">
                <div className="min-w-0 flex-1">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {projectDisplayName || createdBlueprint?.project_name || 'Project Name'}
                  </h1>

                  <div className="mt-5">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-sm font-bold text-gray-900">Innovation platform</h2>
                      {onViewInnovationPlatform ? (
                        <button
                          type="button"
                          onClick={onViewInnovationPlatform}
                          className="text-sm font-medium text-[#43C8F5] underline hover:text-[#2A9CC4] transition-colors"
                        >
                          View
                        </button>
                      ) : null}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
                      {innovationPlatformText ||
                        'Add a job to be done and tension in the Innovation Workspace to define your innovation platform.'}
                    </p>
                  </div>
                </div>

                <div className="w-full lg:w-80 shrink-0 rounded-xl border border-[#EEEEEE] bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Methodology notes</h3>
                  {methodologyFlags.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Info className="w-4 h-4 shrink-0" aria-hidden />
                      <span>No methodology notes added yet.</span>
                    </div>
                  ) : (
                    <>
                      <ul>
                        {visibleMethodologyFlags.map((flag, index) => (
                          <li
                            key={`step3-${flag.type}-${flag.label}-${index}`}
                            className={index > 0 ? 'border-t border-[#EEEEEE]' : undefined}
                          >
                            <div className="flex items-center gap-3 py-3">
                              <MethodologyFlagIcon type={flag.type} />
                              <span className="flex-1 min-w-0 text-sm text-gray-800 leading-snug">
                                {flag.label}
                              </span>
                              <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 whitespace-nowrap">
                                {formatMethodologyTag(flag.evidenced)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {methodologyFlags.length > 3 ? (
                        <div className="flex justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => setShowAllMethodologyNotes((open) => !open)}
                            className="text-sm font-medium text-[#43C8F5] hover:text-[#2A9CC4] inline-flex items-center gap-1"
                          >
                            {showAllMethodologyNotes
                              ? 'View less'
                              : `View all (${methodologyFlags.length})`}
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                showAllMethodologyNotes ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              <RecipeCardTabControls
                tabs={recipeCardTabs}
                activeTabId={activeRecipeTabId}
                brands={recipeCardBrands}
                formats={recipeCardFormats}
                canSave={canSaveBlueprint}
                busy={recipeTabsBusy}
                showBrandFormat={false}
                headerTrailing={
                  <button
                    type="button"
                    onClick={openRecipeCardValidatorModal}
                    className="rounded-full bg-[#655FA8] text-white text-sm font-medium px-5 py-2 hover:bg-[#544fa0] transition-colors whitespace-nowrap"
                  >
                    Recipe Card Validator
                  </button>
                }
                onSelectTab={(tabId) => void handleRecipeTabSelect(tabId)}
                onCreateTab={() => void handleCreateRecipeTab()}
                onRenameTab={(tabId, tabName) => void handleRenameRecipeTab(tabId, tabName)}
                onDeleteTab={(tabId) => void handleDeleteRecipeTab(tabId)}
                onSelectBrand={(tabId, brandId) => void handleSelectRecipeTabBrand(tabId, brandId)}
                onSelectFormat={(tabId, formatId) => void handleSelectRecipeTabFormat(tabId, formatId)}
                onCreateBrand={handleCreateRecipeBrand}
                onCreateFormat={handleCreateRecipeFormat}
              />
            </div>

            <div className="flex-shrink-0 px-8 pt-2 pb-0">
              <div className="flex items-end justify-between gap-4">
                <div className="flex items-end gap-1 overflow-x-auto border-b border-[#D1D1D1] pb-0 flex-1 min-w-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {savedCombinations.map((combination) => {
                    const isActive = combination.id === activeCombinationId;
                    return (
                      <div key={combination.id} className="relative flex-shrink-0 flex items-end">
                        {renamingCombinationId === combination.id && isActive ? (
                          <input
                            autoFocus
                            value={renameCombinationValue}
                            onChange={(e) => setRenameCombinationValue(e.target.value)}
                            onBlur={() => void commitRenameCombination()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void commitRenameCombination();
                              if (e.key === 'Escape') cancelRenameCombination();
                            }}
                            className="mb-0.5 rounded-t-lg border border-[#D1D1D1] px-3 py-2 text-sm min-w-[120px] outline-none focus:border-[#655FA8] focus:ring-2 focus:ring-[#3191C8]/30"
                          />
                        ) : (
                          <div
                            className={`flex items-center rounded-t-lg border text-sm transition-colors ${
                              isActive
                                ? recipeTabActiveClass
                                : `${recipeTabInactiveClass} border hover:border-[#D1D1D1]`
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => handleCombinationChange(combination.id)}
                              className="px-4 py-2.5 outline-none focus-visible:ring-2 focus-visible:ring-[#3191C8]/30 rounded-tl-lg"
                            >
                              {combination.combination_name}
                            </button>
                            {isActive ? (
                              <div className="relative pr-1">
                                <button
                                  ref={combinationMenuButtonRef}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenCombinationMenuId((current) =>
                                      current === combination.id ? null : combination.id
                                    );
                                  }}
                                  className="p-1.5 rounded text-gray-500 hover:bg-gray-100"
                                  aria-label="Combination options"
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </button>
                                <DropdownMenu
                                  open={openCombinationMenuId === combination.id}
                                  onClose={() => setOpenCombinationMenuId(null)}
                                  anchorRef={combinationMenuButtonRef}
                                  minWidth="9rem"
                                >
                                  <MenuItem onClick={() => startRenameCombination(combination)}>
                                    <span className="inline-flex items-center gap-2.5">
                                      <RotateCcw className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
                                      Rename
                                    </span>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => requestDeleteCombination(combination.id)}
                                    disabled={savedCombinations.length <= 1}
                                    destructive
                                  >
                                    <span className="inline-flex items-center gap-2.5">
                                      <Trash2 className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
                                      Delete
                                    </span>
                                  </MenuItem>
                                </DropdownMenu>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={handleNewCombination}
                    disabled={!canSaveBlueprint || isSavingDraftRecipe || !activeRecipeTabId}
                    className={`flex-shrink-0 ${recipeAddTabButtonClass}`}
                    aria-label="Add combination"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div
                  className="flex-shrink-0 mb-1.5 inline-flex items-center rounded-lg border border-[#D1D1D1] bg-[#F5F5F5] p-0.5"
                  role="tablist"
                  aria-label="Recipe page view"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recipePageView === 'recipe-card'}
                    onClick={() => setRecipePageView('recipe-card')}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                      recipePageView === 'recipe-card'
                        ? 'bg-white text-gray-900 font-medium shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {recipePageView === 'recipe-card' ? <Check className="w-3.5 h-3.5 text-[#3191C8]" /> : null}
                    Recipe card
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recipePageView === 'product-idea'}
                    onClick={() => {
                      if (!canToggleProductIdea) return;
                      void (async () => {
                        await persistCurrentCombinationSelections();
                        setRecipePageView('product-idea');
                      })();
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                      recipePageView === 'product-idea'
                        ? 'bg-white text-gray-900 font-medium shadow-sm'
                        : canToggleProductIdea
                          ? 'text-gray-600 hover:text-gray-900'
                          : 'text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!canToggleProductIdea}
                  >
                    {recipePageView === 'product-idea' ? <Check className="w-3.5 h-3.5 text-[#3191C8]" /> : null}
                    Product idea
                  </button>
                </div>
              </div>
              {recipePageView === 'product-idea' && activeCombinationId && !allAttributesSelected ? (
                <p className="px-8 pt-2 text-sm text-red-600">
                  Select an option for every attribute on the Recipe card before generating product ideas.
                </p>
              ) : null}
            </div>

            {recipePageView === 'product-idea' && createdBlueprint?.id ? (
              <div className="flex-1 min-h-0 px-8 pb-4 flex flex-col">
                <ConceptGenerationPage
                  embedded
                  hidePageHeader
                  hideCombinationTabs
                  blueprintId={createdBlueprint.id}
                  combinationId={activeCombinationId}
                  initialCombinationId={activeCombinationId ?? undefined}
                  initialArtifactId={conceptArtifactId ?? undefined}
                  combinationAttributesComplete={allAttributesSelected}
                  onOpenProductIdeaGenerator={openProductIdeaGeneratorModal}
                  onOpenProductIdeaValidator={openProductIdeaValidatorModal}
                  onBack={() => setRecipePageView('recipe-card')}
                />
              </div>
            ) : (
              <div className="flex-1 min-h-0 px-8 pb-4 flex flex-col">
                <div className="flex-1 min-h-0 rounded-2xl border border-[#D1D1D1] bg-[#F5F5F5] flex flex-col overflow-hidden">
                  <div className="flex-1 overflow-y-auto p-5">
                    {isLoadingOptions && (
                      <div className="rounded-lg border border-[#D1D1D1] bg-white px-3 py-2 text-xs text-gray-500 mb-4">
                        Loading options...
                      </div>
                    )}

                    {!activeCombinationId && savedCombinations.length === 0 ? (
                      <div className="text-center py-16 text-gray-500 text-sm">
                        Add a combination to start selecting options for this tab.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {attributes.map((attr) => {
                          const allOutputs = attributeOutputHistory[attr.id] || [];
                          const activeOutputVersionId = getActiveOutputVersionId(
                            activeOutputVersionByCombination,
                            activeCombinationId,
                            attr.id
                          );
                          const activeOutputVersion =
                            allOutputs.find((version) => version.id === activeOutputVersionId) ?? null;
                          const currentOutputVersion = allOutputs.find((version) => version.is_current) ?? null;
                          const displayedOptions = sortRecipeAttributeOptions(
                            activeOutputVersion
                              ? [...(activeOutputVersion.options || [])]
                              : attributeOptions.filter((opt) => opt.attribute_set === attr.id)
                          );
                          const exploratoryOptions = displayedOptions.filter((opt) =>
                            (opt.option_notes || '').includes('evidenced=')
                          );
                          const regularOptions = displayedOptions.filter(
                            (opt) => !(opt.option_notes || '').includes('evidenced=')
                          );
                          const manualChips = regularOptions.filter(
                            (o) => !(o.generation_source === 'ai' && o.ai_category)
                          );
                          const aiCategoryOrder = ['established', 'emerging', 'breakthrough'] as const;
                          const aiLabels: Record<(typeof aiCategoryOrder)[number], string> = {
                            established: 'Established',
                            emerging: 'Emerging',
                            breakthrough: 'Breakthrough',
                          };
                          const hasCurrentAiOutput =
                            regularOptions.some((o) => o.generation_source === 'ai' && Boolean(o.output_version)) ||
                            allOutputs.length > 0;
                          const availableOutputs = allOutputs.length > 0 ? allOutputs : currentOutputVersion ? [currentOutputVersion] : [];
                          const hasAiGrouped = regularOptions.some((o) => {
                            if (o.generation_source !== 'ai' || !o.ai_category) return false;
                            return (
                              o.ai_category === 'established' ||
                              o.ai_category === 'emerging' ||
                              o.ai_category === 'breakthrough'
                            );
                          });
                          const hasRegularContent = manualChips.length > 0 || hasAiGrouped;
                          const renderOptionChip = (opt: RecipeAttributeOption) => {
                            const isSelected = isOptionSelected(selectedOptionByAttribute, attr.id, opt.id);
                            return (
                              <button
                                type="button"
                                key={opt.id}
                                onClick={() => selectOption(attr.id, opt.id)}
                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border transition ${
                                  isSelected
                                    ? 'bg-white border-[#655FA8] text-[#2f3035] ring-1 ring-[#655FA8]/30'
                                    : 'bg-[#ddd7ef] border-transparent text-[#2f3035] hover:border-[#655FA8]/40'
                                }`}
                              >
                                {formatRecipeOptionLabel(opt)}
                              </button>
                            );
                          };
                          return (
                            <div
                              key={attr.id}
                              className="rounded-xl border border-[#D1D1D1] bg-white overflow-hidden min-h-[200px] shadow-sm"
                            >
                              <div className="px-4 py-3 border-b border-[#EEEEEE] flex items-center justify-between gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {attr.attribute_name}
                                  {attr.is_user_added ? (
                                    <span className="ml-2 text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                                      User added
                                    </span>
                                  ) : null}
                                </h4>
                                <div className="flex items-center gap-1.5 shrink-0 relative">
                                  {hasCurrentAiOutput ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          const loaded = attributeOutputHistory[attr.id] || await loadAttributeOutputHistory(attr.id);
                                          if (loaded.length > 0) {
                                            setOpenOutputMenuAttributeId(
                                              openOutputMenuAttributeId === attr.id ? null : attr.id
                                            );
                                          }
                                        }}
                                        className="text-xs text-[#655FA8] underline hover:text-[#544fa0]"
                                      >
                                        {activeOutputVersion?.label || 'Previous Output'}
                                      </button>
                                      {openOutputMenuAttributeId === attr.id && availableOutputs.length > 0 ? (
                                        <div className="absolute right-0 top-8 z-20 w-36 rounded-xl border border-[#D1D1D1] bg-white shadow-lg overflow-hidden">
                                          {availableOutputs.map((version) => (
                                            <button
                                              key={version.id}
                                              type="button"
                                              onClick={() => {
                                                if (activeCombinationId == null) return;
                                                setActiveOutputVersionByCombination((prev) =>
                                                  setActiveOutputVersionForCombination(
                                                    prev,
                                                    activeCombinationId,
                                                    attr.id,
                                                    version.id
                                                  )
                                                );
                                                setOpenOutputMenuAttributeId(null);
                                              }}
                                              className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                                                activeOutputVersionId === version.id || (!activeOutputVersionId && version.is_current)
                                                  ? 'text-[#655FA8] font-medium'
                                                  : 'text-gray-700'
                                              }`}
                                            >
                                              {version.label}
                                            </button>
                                          ))}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => openRegenerateAttributeModal(attr)}
                                    disabled={!canSaveBlueprint}
                                    className="w-8 h-8 rounded-full text-gray-400 hover:text-[#655FA8] hover:bg-gray-50 flex items-center justify-center disabled:opacity-40"
                                    aria-label={`Regenerate ${attr.attribute_name}`}
                                    title="Regenerate attribute options"
                                  >
                                    <Sparkles className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => requestDeleteAttribute(attr.id)}
                                    disabled={!canSaveBlueprint}
                                    className="w-8 h-8 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center disabled:opacity-40"
                                    aria-label={`Delete ${attr.attribute_name}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                              <div className="p-4 space-y-3">
                                <div className="space-y-3 min-h-[30px]">
                                  {!hasRegularContent && exploratoryOptions.length === 0 ? (
                                    <span className="text-xs text-gray-400">No options yet.</span>
                                  ) : (
                                    <>
                                      {manualChips.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                          {manualChips.map(renderOptionChip)}
                                        </div>
                                      ) : null}
                                      {aiCategoryOrder.map((cat) => {
                                        const list = regularOptions.filter(
                                          (o) => o.generation_source === 'ai' && o.ai_category === cat
                                        );
                                        if (!list.length) return null;
                                        return (
                                          <div key={cat} className="space-y-1.5">
                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                              {aiLabels[cat]}
                                            </p>
                                            <div className="flex flex-wrap gap-2">{list.map(renderOptionChip)}</div>
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}
                                </div>
                                {exploratoryOptions.length > 0 ? (
                                  <div className="space-y-2 pt-1">
                                    {exploratoryOptions.map((opt) => {
                                      const evidenced = (opt.option_notes || '').includes('evidenced=true');
                                      const isSelected = isOptionSelected(selectedOptionByAttribute, attr.id, opt.id);
                                      return (
                                        <div
                                          key={opt.id}
                                          className={`rounded-lg border p-3 transition cursor-pointer ${
                                            isSelected
                                              ? 'border-[#655FA8] bg-[#655FA8]/5'
                                              : 'border-[#D1D1D1] bg-[#F5F5F5] hover:border-[#655FA8]/40'
                                          }`}
                                          onClick={() => selectOption(attr.id, opt.id)}
                                        >
                                          <div className="flex items-start justify-between gap-2">
                                            <div>
                                              <p className="text-xs font-semibold text-gray-900">
                                                Exploratory input
                                              </p>
                                              <p className="text-[10px] text-gray-500 mt-0.5">
                                                {formatMethodologyTag(evidenced)}
                                              </p>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                deleteOption(opt.id);
                                              }}
                                              disabled={!canSaveBlueprint}
                                              className="text-gray-400 hover:text-red-600 disabled:opacity-40"
                                              aria-label="Remove exploratory input"
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                          <p className="text-xs text-gray-700 mt-2 whitespace-pre-wrap">
                                            {formatRecipeOptionLabel(opt)}
                                          </p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => openExploratoryModal(attr.id)}
                                  disabled={!canSaveBlueprint || !activeCombinationId}
                                  className="text-sm font-medium text-[#3191C8] hover:text-[#2a7fb0] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  + Add exploratory input
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="px-5 py-4 border-t border-[#D1D1D1] bg-white flex items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="rounded-full border border-[#3191C8] text-[#3191C8] text-sm font-medium px-6 py-2 hover:bg-[#3191C8]/5 transition-colors"
                    >
                      Exit
                    </button>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">
                        {selectedAttributeCount} of {attributes.length} attributes selected
                      </span>
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        disabled={!activeCombinationId || !hasAnyCombinationSelection(selectedOptionByAttribute)}
                        className="rounded-full border border-[#3191C8] text-[#3191C8] text-sm font-medium px-5 py-2 hover:bg-[#3191C8]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear Selection
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void (async () => {
                            if (!activeCombinationId) {
                              setError('Create or select a combination first.');
                              return;
                            }
                            const saved = await persistCurrentCombinationSelections();
                            if (saved) {
                              setRecipePageView('product-idea');
                            }
                          })();
                        }}
                        disabled={
                          !canSaveBlueprint ||
                          !activeCombinationId ||
                          isSavingDraftRecipe ||
                          !allAttributesSelected
                        }
                        className={`rounded-full text-sm font-semibold px-6 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          allAttributesSelected
                            ? 'bg-[#3191C8] text-white hover:bg-[#2a7fb0]'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        Go to product idea
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {step === 1 && (
        <div className="border-t border-gray-200 bg-white px-6 py-4 flex items-center justify-end">
          <button
            type="button"
            onClick={() => void handleNext()}
            disabled={
              isSaving ||
              step1ContinueBusy ||
              !canAdvanceFromStep1
            }
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50 inline-flex items-center gap-2"
          >
            {isSaving || step1ContinueBusy ? 'Please wait…' : 'Configure Recipe Card'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {saveNotice && (
        <div className="pointer-events-none fixed right-6 top-20 z-[15000]">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800 shadow-lg">
            {saveNotice}
          </div>
        </div>
      )}

      {showRecipeCardBuilderModal && (
        <div
          className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="recipe-card-builder-title"
          onClick={() => setShowRecipeCardBuilderModal(false)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="recipe-card-builder-title" className="text-xl font-bold text-gray-900">
                  Recipe Card Builder
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Generate diverse product recipes—formats, ingredients, claims, pricing—ranging from
                  safe to breakthrough options.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowRecipeCardBuilderModal(false)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <CommercialProductList
                products={recipeCardBuilderProducts}
                busy={recipeCardBuilderProductsLoading || !canSaveBlueprint || isSaving || isRecipeAiGenerating}
                onExplore={handleRecipeCardBuilderProduct}
                onStart={handleRecipeCardBuilderProduct}
              />
            </div>
          </div>
        </div>
      )}

      {showGenerationSummaryModal && (
        <div
          className="fixed inset-0 z-[15500] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="generation-summary-title"
        >
          <div
            className="w-full max-w-3xl rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 id="generation-summary-title" className="text-xl font-semibold text-gray-900">
                {generationMode === 'ai' ? 'Ready to generate options?' : 'Ready for crowd generation?'}
              </h2>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {generationMode === 'ai' ? (
                  <>
                    Attributes where you entered details will use your text only—we won&apos;t request AI options
                    for those. Everything else will get generated options.
                  </>
                ) : (
                  <>
                    Attributes where you entered details will use your text only. Crowd options will be requested
                    for the rest.
                  </>
                )}
              </p>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-5">
              {attributes.length === 0 ? (
                <p className="text-sm text-gray-500">No attributes found.</p>
              ) : (
                <>
                  <section>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {generationMode === 'ai' ? 'Will get AI options' : 'Will use crowd generation'}
                      {' '}
                      <span className="font-normal text-gray-500">({attributesForGeneration.length})</span>
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5 mb-2">
                      No details entered—you&apos;ll receive generated options for these.
                    </p>
                    {attributesForGeneration.length === 0 ? (
                      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        None—all attributes have details filled in, so nothing will be generated.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {attributesForGeneration.map((attr) => (
                          <li
                            key={attr.id}
                            className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2"
                          >
                            <p className="text-sm font-medium text-gray-900">{attr.attribute_name}</p>
                            {attr.constraints_text?.trim() ? (
                              <p className="text-xs text-gray-600 mt-1">
                                <span className="font-medium">Constraints:</span> {attr.constraints_text}
                              </p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Using your details (no generated options)
                      {' '}
                      <span className="font-normal text-gray-500">({attributesSkippedDueToDetails.length})</span>
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5 mb-2">
                      You filled in the details box—we&apos;ll keep your answer and won&apos;t load new options.
                    </p>
                    {attributesSkippedDueToDetails.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">None</p>
                    ) : (
                      <ul className="space-y-2">
                        {attributesSkippedDueToDetails.map((attr) => (
                          <li
                            key={attr.id}
                            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                          >
                            <p className="text-sm font-medium text-gray-900">{attr.attribute_name}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              <span className="font-medium">Your details:</span> {attr.details_text?.trim()}
                            </p>
                            {attr.constraints_text?.trim() ? (
                              <p className="text-xs text-gray-600 mt-1">
                                <span className="font-medium">Constraints:</span> {attr.constraints_text}
                              </p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              {generationMode === 'ai' ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowGenerationSummaryModal(false)}
                    disabled={isRecipeAiGenerating}
                    className="rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-5 py-2 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void runRecipeAiGeneration()}
                    disabled={
                      isRecipeAiGenerating ||
                      !canSaveBlueprint ||
                      attributesForGeneration.length === 0
                    }
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {isRecipeAiGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating…
                      </>
                    ) : (
                      'Generate recipe options'
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowGenerationSummaryModal(false)}
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isRecipeAiGenerating && (
        <div className="fixed inset-0 z-[16000] flex items-center justify-center bg-black/50 p-6">
          <div className="max-w-sm rounded-2xl bg-white px-8 py-6 text-center shadow-xl flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-indigo-600" aria-hidden />
            <p className="text-sm font-semibold text-gray-900">Analyzing your recipe…</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Generating options for your attributes. This can take up to a minute.
            </p>
          </div>
        </div>
      )}

      {showJtbdPickerModal && (
        <div
          className="fixed inset-0 z-[16600] flex items-center justify-center bg-black/45 p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="jtbd-picker-title"
          onClick={() => setShowJtbdPickerModal(false)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0">
              <h2 id="jtbd-picker-title" className="text-lg font-semibold text-gray-900">
                Pick a job to be done
              </h2>
              <button
                type="button"
                onClick={() => setShowJtbdPickerModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0">
              <ul className="space-y-2">
                {jtbdStatements.map((statement, idx) => (
                  <li key={`jtbd-${idx}-${statement.slice(0, 48)}`}>
                    <button
                      type="button"
                      className="w-full text-left rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 hover:border-indigo-300 hover:bg-indigo-50/60 transition-colors"
                      onClick={() => {
                        setJob(statement);
                        setShowJtbdPickerModal(false);
                        showTransientSaveNotice('Job to be done filled in — save when you are ready.', 3500);
                      }}
                    >
                      {statement}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setShowJtbdPickerModal(false)}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showBenefitPickerModal && (
        <div
          className="fixed inset-0 z-[16600] flex items-center justify-center bg-black/45 p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="benefit-picker-title"
          onClick={() => setShowBenefitPickerModal(false)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0">
              <h2 id="benefit-picker-title" className="text-lg font-semibold text-gray-900">
                Pick a benefit
              </h2>
              <button
                type="button"
                onClick={() => setShowBenefitPickerModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0">
              <ul className="space-y-2">
                {benefitStatements.map((item, idx) => {
                  const text = (item?.benefit_text || '').trim();
                  if (!text) return null;
                  return (
                    <li key={`benefit-${idx}-${text.slice(0, 48)}`}>
                      <button
                        type="button"
                        className="w-full text-left rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 hover:border-indigo-300 hover:bg-indigo-50/60 transition-colors"
                        onClick={() => {
                          setBenefit(text);
                          setShowBenefitPickerModal(false);
                          showTransientSaveNotice('Benefit filled in — save when you are ready.', 3500);
                        }}
                      >
                        {text}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setShowBenefitPickerModal(false)}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showTensionPickerModal && (
        <div
          className="fixed inset-0 z-[16600] flex items-center justify-center bg-black/45 p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tension-picker-title"
          onClick={() => setShowTensionPickerModal(false)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0">
              <h2 id="tension-picker-title" className="text-lg font-semibold text-gray-900">
                Pick a tension
              </h2>
              <button
                type="button"
                onClick={() => setShowTensionPickerModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0">
              <ul className="space-y-2">
                {tensionStatements.map((item, idx) => {
                  const text = (item?.tension_text || '').trim();
                  if (!text) return null;
                  return (
                    <li key={`tension-${idx}-${text.slice(0, 48)}`}>
                      <button
                        type="button"
                        className="w-full text-left rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 hover:border-indigo-300 hover:bg-indigo-50/60 transition-colors"
                        onClick={() => {
                          setTension(text);
                          setShowTensionPickerModal(false);
                          showTransientSaveNotice('Tension filled in — save when you are ready.', 3500);
                        }}
                      >
                        {text}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setShowTensionPickerModal(false)}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCrowdWorkInProgressModal && (
        <div
          className="fixed inset-0 z-[12200] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crowd-wip-title"
          onClick={() => setShowCrowdWorkInProgressModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-200">
              <h2 id="crowd-wip-title" className="text-lg font-semibold text-gray-900">
                Work in progress
              </h2>
              <button
                type="button"
                onClick={() => setShowCrowdWorkInProgressModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600">
                Generate with Crowd is not available yet. Please check back soon.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowCrowdWorkInProgressModal(false)}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showFormatIdeasWorkInProgressModal && (
        <div
          className="fixed inset-0 z-[12200] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="format-ideas-wip-title"
          onClick={() => setShowFormatIdeasWorkInProgressModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-200">
              <h2 id="format-ideas-wip-title" className="text-lg font-semibold text-gray-900">
                Work in progress
              </h2>
              <button
                type="button"
                onClick={() => setShowFormatIdeasWorkInProgressModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600">
                Generate format ideas is not available yet. Please check back soon.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowFormatIdeasWorkInProgressModal(false)}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecipeCardValidatorModal && (
        <div
          className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="recipe-card-validator-title"
          onClick={() => setShowRecipeCardValidatorModal(false)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="recipe-card-validator-title" className="text-xl font-bold text-gray-900">
                  Recipe Card Validator
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Consumers vote on recipe options to construct their ideal product for the Innovation Platform.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowRecipeCardValidatorModal(false)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <CommercialProductList
                products={recipeCardValidatorProducts}
                busy={recipeCardValidatorProductsLoading}
                onExplore={handleRecipeCardValidatorProduct}
                onStart={handleRecipeCardValidatorProduct}
              />
            </div>
          </div>
        </div>
      )}

      {showProductIdeaGeneratorModal && (
        <div
          className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-idea-generator-title"
          onClick={() => setShowProductIdeaGeneratorModal(false)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="product-idea-generator-title" className="text-xl font-bold text-gray-900">
                  Product Idea Generator
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Bring all the selected elements of your Innovation Recipe Card to life. Fully written concept board plus pack/product visual, that turns your product idea into reality for consumers to react to.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowProductIdeaGeneratorModal(false)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <CommercialProductList
                products={productIdeaGeneratorProducts}
                busy={productIdeaGeneratorProductsLoading}
                onExplore={handleProductIdeaGeneratorProduct}
                onStart={handleProductIdeaGeneratorProduct}
              />
            </div>
          </div>
        </div>
      )}

      {showProductIdeaValidatorModal && (
        <div
          className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-idea-validator-title"
          onClick={() => setShowProductIdeaValidatorModal(false)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="product-idea-validator-title" className="text-xl font-bold text-gray-900">
                  Product Idea Validator
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Test how well your new product idea lands in the eyes of the consumer.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowProductIdeaValidatorModal(false)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <CommercialProductList
                products={productIdeaValidatorProducts}
                busy={productIdeaValidatorProductsLoading}
                onExplore={handleProductIdeaValidatorProduct}
                onStart={handleProductIdeaValidatorProduct}
              />
            </div>
          </div>
        </div>
      )}

      {showDeleteCombinationModal && (
        <div
          className="fixed inset-0 z-[12200] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-combination-title"
          onClick={() => !isSavingDraftRecipe && setShowDeleteCombinationModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-200">
              <h2 id="delete-combination-title" className="text-lg font-semibold text-gray-900">
                Delete combination?
              </h2>
              <button
                type="button"
                onClick={() => setShowDeleteCombinationModal(false)}
                disabled={isSavingDraftRecipe}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600">
                This will permanently delete this combination and its saved selections.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteCombinationModal(false)}
                disabled={isSavingDraftRecipe}
                className="rounded-full border border-gray-300 bg-white text-sm font-medium px-5 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteCombination()}
                disabled={isSavingDraftRecipe}
                className="rounded-full bg-red-600 text-white text-sm font-semibold px-5 py-2 hover:bg-red-700 disabled:opacity-50"
              >
                {isSavingDraftRecipe ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnderConstructionModal && (
        <div
          className="fixed inset-0 z-[16500] flex items-center justify-center bg-black/45 p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="under-construction-title"
          onClick={() => setShowUnderConstructionModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-indigo-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between gap-3">
                <h2 id="under-construction-title" className="text-lg font-semibold text-gray-900">
                {assistCheckBusy
                  ? 'Checking project research'
                  : assistResearchCompleted && assistKind === 'job'
                    ? JOB_ASSIST_READY_TITLE
                    : assistResearchCompleted && assistKind === 'tension'
                      ? TENSION_ASSIST_READY_TITLE
                      : assistResearchCompleted && assistKind === 'benefit'
                        ? BENEFIT_ASSIST_READY_TITLE
                    : assistResearchCompleted
                      ? 'Research complete'
                      : 'Research in progress'}
              </h2>
              <button
                type="button"
                onClick={() => setShowUnderConstructionModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              {assistCheckBusy ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  Checking latest job status...
                </div>
              ) : assistResearchCompleted && assistKind === 'job' ? (
                <div className="space-y-4">
                  {!jtbdGenerationAllowed || jtbdStatements.length > 0 ? (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Jobs to be done were already generated for this project and market. Choose one
                        from the list next to the Job field.
                      </p>
                      {jtbdStatements.length > 0 && (
                        <button
                          type="button"
                          className="rounded-full border border-indigo-300 text-indigo-700 text-sm font-semibold px-6 py-2.5 hover:bg-indigo-50"
                          onClick={() => {
                            setShowUnderConstructionModal(false);
                            setShowJtbdPickerModal(true);
                          }}
                        >
                          View jobs to be done
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed">{JOB_ASSIST_READY_BODY}</p>
                      <button
                        type="button"
                        disabled={jtbdGenerateBusy || !canSaveBlueprint || !createdBlueprint?.id}
                        className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-6 py-2.5 shadow-sm hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
                        onClick={() => void handleJtbdGenerate()}
                      >
                        {jtbdGenerateBusy ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                            Generating…
                          </>
                        ) : (
                          'Generate'
                        )}
                      </button>
                    </>
                  )}
                </div>
              ) : assistResearchCompleted && assistKind === 'tension' ? (
                <div className="space-y-4">
                  {!tensionsGenerationAllowed || tensionStatements.length > 0 ? (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Tensions were already generated for this project, market, and job. Choose one
                        from the list next to the Tension field.
                      </p>
                      {tensionStatements.length > 0 && (
                        <button
                          type="button"
                          className="rounded-full border border-indigo-300 text-indigo-700 text-sm font-semibold px-6 py-2.5 hover:bg-indigo-50"
                          onClick={() => {
                            setShowUnderConstructionModal(false);
                            setShowTensionPickerModal(true);
                          }}
                        >
                          View tensions
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed">{TENSION_ASSIST_READY_BODY}</p>
                      <button
                        type="button"
                        disabled={tensionGenerateBusy || !canSaveBlueprint || !createdBlueprint?.id}
                        className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-6 py-2.5 shadow-sm hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
                        onClick={() => void handleTensionsGenerate()}
                      >
                        {tensionGenerateBusy ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                            Generating…
                          </>
                        ) : (
                          'Generate'
                        )}
                      </button>
                    </>
                  )}
                </div>
              ) : assistResearchCompleted && assistKind === 'benefit' ? (
                <div className="space-y-4">
                  {!benefitsGenerationAllowed || benefitStatements.length > 0 ? (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Benefits were already generated for this project, market, job, and tension. Choose
                        one from the list next to the Benefit field.
                      </p>
                      {benefitStatements.length > 0 && (
                        <button
                          type="button"
                          className="rounded-full border border-indigo-300 text-indigo-700 text-sm font-semibold px-6 py-2.5 hover:bg-indigo-50"
                          onClick={() => {
                            setShowUnderConstructionModal(false);
                            setShowBenefitPickerModal(true);
                          }}
                        >
                          View benefits
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed">{BENEFIT_ASSIST_READY_BODY}</p>
                      <button
                        type="button"
                        disabled={benefitGenerateBusy || !canSaveBlueprint || !createdBlueprint?.id}
                        className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-6 py-2.5 shadow-sm hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
                        onClick={() => void handleBenefitsGenerate()}
                      >
                        {benefitGenerateBusy ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                            Generating…
                          </>
                        ) : (
                          'Generate'
                        )}
                      </button>
                    </>
                  )}
                </div>
              ) : assistResearchCompleted ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Research for this project is completed.
                  </p>
                  <button
                    type="button"
                    className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700"
                  >
                    Continue
                  </button>
                </div>
              ) : assistRequiresJobLock ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Please lock your Job to be done before continuing.
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleLockJobToBeDone()}
                    disabled={jobLockBusy || !job.trim() || !canSaveBlueprint || !createdBlueprint?.id}
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-6 py-2.5 shadow-sm hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {jobLockBusy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        Locking…
                      </>
                    ) : (
                      'Lock Job to be done'
                    )}
                  </button>
                </div>
              ) : assistRequiresTensionLock ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Please lock your Tension before generating benefits.
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleLockTension()}
                    disabled={
                      tensionLockBusy || !tension.trim() || !canSaveBlueprint || !createdBlueprint?.id
                    }
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-6 py-2.5 shadow-sm hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {tensionLockBusy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        Locking…
                      </>
                    ) : (
                      'Lock Tension'
                    )}
                  </button>
                </div>
              ) : assistModalMessage ? (
                <p className="text-sm text-gray-600 leading-relaxed">{assistModalMessage}</p>
              ) : (
                <p className="text-sm text-gray-600 leading-relaxed">
                  We are still gathering information for your project. Check again in 5 minutes.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowUnderConstructionModal(false)}
                disabled={assistCheckBusy}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold px-5 py-2"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueprintBuilderV2;
